// metric/groceryProcessorMetric.js - Grocery processing for metric measurement system
import {
  normalizeToMetricBase,
  calculateTotalCostMetric,
  getConsolidationUnit,
  normalizePartUnit,
  convertPartsToWholes,
  getPartToWholeConfig,
  isDryIngredient,
  getDryIngredientDensity,
  // categorizeProductsMetric // Removed import
} from "./groceryUtilsMetric";
import {
  normalizeIngredientName,
  getIngredientCategory,
  findProductMetadata,
  getDensity,
} from "../groceryUtils";
import { processUnmatchedIngredientsWithAI } from "../aiGroceryFallback";
import {
  normalizeCategory,
  PRODUCT_METADATA,
  FOOD_DENSITIES,
  HEAD_WEIGHTS,
} from "../groceryConstants";
import {
  classifyItemStorage,
  buildIngredientFormMap,
  resolveIngredientForm,
} from "../storageClassifier";
import {
  GROCERY_HOST,
  MODEL_CONFIG,
} from "../../services/apiConfig";
import { fetchFromBackend } from "../../services/aiProxyClient";
import countryFoodPreferences from "../country_food_preferences.json";

/**
 * Default average weights for unit products by category (in grams)
 * Used when avgWeight is not available in product data or PRODUCT_METADATA
 */
const DEFAULT_AVG_WEIGHTS_METRIC = {
  'Produce': 150,      // General produce like vegetables, fruits
  'Meat & Seafood': 200,
  'Dairy, Alternatives & Eggs': 60, // Eggs, cheese slices, etc.
  'Bakery': 50,        // Bread rolls, pastries
  'Frozen': 100,
  'Pantry': 100,
  'Condiments': 50,
  'Other': 100,
};

/**
 * Get avgWeight for a product, with fallback to PRODUCT_METADATA and category defaults
 * @param {Object} size - Product size object
 * @param {String} productName - Product name for metadata lookup
 * @param {String} category - Product category for default fallback
 * @returns {Number|null} Average weight in grams, or null if truly unavailable
 */
const getAvgWeightWithFallback = (size, productName, category) => {
  // 1. Try size.avgWeight first
  const sizeAvgWeight = parseFloat(size?.avgWeight);
  if (!isNaN(sizeAvgWeight) && sizeAvgWeight > 0) {
    return sizeAvgWeight;
  }
  
  // 2. Try PRODUCT_METADATA
  if (productName) {
    const metadata = findProductMetadata(productName);
    if (metadata?.avgWeight?.metric) {
      return metadata.avgWeight.metric;
    }
  }
  
  // 3. Fallback to category defaults
  const normalizedCategory = normalizeCategory(category || 'Other');
  return DEFAULT_AVG_WEIGHTS_METRIC[normalizedCategory] || DEFAULT_AVG_WEIGHTS_METRIC['Other'];
};

/**
 * Default category-based pricing (per kg/L for estimation)
 */
const CATEGORY_PRICING_DEFAULTS = {
  'Produce': 2.50,
  'Meat & Seafood': 8.00,
  'Dairy, Alternatives & Eggs': 3.50,
  'Bakery': 3.00,
  'Frozen': 5.00,
  'Pantry': 2.50,
  'Condiments': 4.00,
  'Other': 3.00,
};

// ============================================================================
// CANONICAL-UNIT COERCION (Phase B6)
// ----------------------------------------------------------------------------
// `consolidateByProductId` keys by `${productId}_${measurementType}`. When the
// same Typesense product arrives twice with different measurementTypes (e.g.
// "ground pepper 2g" + "ground pepper 50ml" or "garlic 2 cloves" + "garlic 50g"),
// two rows survive consolidation and show up as duplicates in the grocery list.
//
// These helpers coerce every matched item to a single canonical measurement type
// per product BEFORE consolidation, so the key collapses naturally. The strategy:
//   • Canonical type is decided by the matched Typesense product type:
//       - product.sizes[0].type === 'package' | 'unit' -> 'count'
//       - product.sizes[0].type === 'weight'           -> 'weight'
//       - product.sizes[0].type === 'volume'           -> 'volume'
//   • PART_TO_WHOLE products (garlic head, bread loaf, etc.) also canonicalize
//     to 'count' (the whole-unit), so part-quantities (cloves/slices) align
//     with whole-unit purchases.
//   • For products without a Typesense match, we fall back to the original
//     measurementType.
//
// All coercions are logged with the `[CanonicalCoerce]` prefix for traceability.
// ============================================================================

/**
 * Get per-unit avgWeight in grams for a matched item, used by canonical
 * coercion to convert count <-> weight. Tries (in priority order):
 *   1. PRODUCT_METADATA by ingredient name (e.g. "garlic" -> 50g/head)
 *   2. PRODUCT_METADATA by product display name (e.g. "Ajo Morado Malla")
 *   3. PRODUCT_METADATA by productId slug
 *   4. HEAD_WEIGHTS by ingredient name (whole-head/loaf reference table)
 *   5. product.sizes[0].avgWeight (package weight — caution: not per-unit)
 *   6. Category default for produce (100g/piece)
 */
const getProductAvgWeight = (item) => {
  // 1. PRODUCT_METADATA by ingredient name (best signal)
  const metaByName = item.name ? findProductMetadata(item.name) : null;
  if (metaByName?.avgWeight?.metric) {
    return metaByName.avgWeight.metric;
  }
  // 2. PRODUCT_METADATA by product display name
  const metaByProductName = item.productName ? findProductMetadata(item.productName) : null;
  if (metaByProductName?.avgWeight?.metric) {
    return metaByProductName.avgWeight.metric;
  }
  // 3. PRODUCT_METADATA by productId slug (rare match)
  if (item.productId && item.productId !== item.name && item.productId !== item.productName) {
    const metaById = findProductMetadata(item.productId);
    if (metaById?.avgWeight?.metric) {
      return metaById.avgWeight.metric;
    }
  }
  // 4. HEAD_WEIGHTS (whole-head reference) — only for part-to-whole items
  const ptw = getPartToWholeConfig(item.name || item.productName || '');
  if (ptw) {
    const nameLower = (item.name || item.productName || '').toLowerCase();
    for (const [key, weight] of Object.entries(HEAD_WEIGHTS)) {
      if (nameLower.includes(key)) return weight;
    }
  }
  // 5. Package weight from Typesense (less ideal — package, not per-unit)
  if (item.product?.sizes?.[0]?.avgWeight) {
    const pkgAvgWeight = parseFloat(item.product.sizes[0].avgWeight);
    if (!isNaN(pkgAvgWeight) && pkgAvgWeight > 0) {
      return pkgAvgWeight;
    }
  }
  // 6. Category default for produce
  const category = (item.category || '').toLowerCase();
  if (category.includes('produce') || category.includes('vegetable')) {
    return 100;
  }
  return null;
};

/**
 * Determine the canonical measurement type for a matched item based on its
 * Typesense product type. Items without a Typesense match keep their
 * original measurementType.
 */
const getCanonicalMeasurementType = (item) => {
  const productType = item.product?.sizes?.[0]?.type;
  if (productType === 'package' || productType === 'unit') return 'count';
  if (productType === 'weight') return 'weight';
  if (productType === 'volume') return 'volume';
  // PART_TO_WHOLE items canonicalize to count (whole-unit)
  const ptw = getPartToWholeConfig(item.name || item.productName || '');
  if (ptw) return 'count';
  return item.measurementType;
};

/**
 * Coerce an item's totalQty/unit/measurementType into its canonical form.
 * Logs every coercion with the `[CanonicalCoerce]` prefix. Returns the item
 * unchanged if it's already canonical or if coercion is impossible.
 */
const coerceToCanonicalUnit = (item) => {
  const current = item.measurementType;
  const canonical = getCanonicalMeasurementType(item);
  if (!canonical || canonical === current) return item;

  const productLabel = item.productName || item.name || 'unknown';
  const before = `${item.totalQty} ${item.unit} (${current})`;

  // count -> weight: convert pieces to grams using avgWeight
  if (current === 'count' && canonical === 'weight') {
    const avgWeight = getProductAvgWeight(item);
    if (!avgWeight) {
      console.log(`[CanonicalCoerce] ⚠️ Cannot coerce count→weight for "${productLabel}" (no avgWeight) — keeping ${before}`);
      return item;
    }
    const newQty = Math.round(item.totalQty * avgWeight);
    console.log(`[CanonicalCoerce] count→weight: "${productLabel}" ${before} → ${newQty} g (× ${avgWeight}g/piece)`);
    return { ...item, totalQty: newQty, unit: 'g', measurementType: 'weight' };
  }

  // weight -> count: intentionally NOT done here.
  //
  // PRODUCT_METADATA avgWeight (used by getProductAvgWeight) represents the per-individual-
  // kitchen-piece weight (e.g. 150g per pork fillet), whereas the Typesense product unit
  // represents the actual store purchase unit (e.g. a 247g tray).  Converting with the
  // PRODUCT_METADATA weight and then computing leftovers with the Typesense weight causes
  // a systematic over-purchase: ceil(720/150)=5 trays instead of ceil(720/247)=3 trays.
  //
  // The correct conversion is handled in calculatePurchaseQuantitiesMetric (Phase 5) where
  // the "unit + weight-need" block uses getAvgWeightWithFallback (Typesense-first) and also
  // captures originalWeightNeed so the leftover calculation stays accurate.
  //
  // Keeping items as 'weight' here does NOT break consolidation: two weight-specified
  // recipes for the same product already share the same key (productId_weight).  The rare
  // case of one recipe using count + another using grams is covered by the count→weight
  // coercion above (which IS correct) plus the legacy count↔weight merge fallback below.
  if (current === 'weight' && canonical === 'count') {
    console.log(`[CanonicalCoerce] weight→count skipped for "${productLabel}" — will be resolved in Phase 5 with Typesense avgWeight`);
    return item;
  }

  // volume -> weight: convert ml to grams using density (dry density for spices, FOOD_DENSITIES otherwise)
  if (current === 'volume' && canonical === 'weight') {
    const ingName = item.name || item.productName || '';
    let density = isDryIngredient(ingName) ? getDryIngredientDensity(ingName) : null;
    if (!density) {
      density = getDensity(ingName);
    }
    if (!density || density <= 0) {
      console.log(`[CanonicalCoerce] ⚠️ Cannot coerce volume→weight for "${productLabel}" (no density) — keeping ${before}`);
      return item;
    }
    const newQty = Math.round(item.totalQty * density);
    console.log(`[CanonicalCoerce] volume→weight: "${productLabel}" ${before} → ${newQty} g (× ${density} g/ml)`);
    return { ...item, totalQty: newQty, unit: 'g', measurementType: 'weight' };
  }

  // weight -> volume: convert grams to ml using density (rare path; supported for completeness)
  if (current === 'weight' && canonical === 'volume') {
    const ingName = item.name || item.productName || '';
    const density = isDryIngredient(ingName) ? getDryIngredientDensity(ingName) : getDensity(ingName);
    if (!density || density <= 0) {
      console.log(`[CanonicalCoerce] ⚠️ Cannot coerce weight→volume for "${productLabel}" (no density) — keeping ${before}`);
      return item;
    }
    const newQty = Math.round(item.totalQty / density);
    console.log(`[CanonicalCoerce] weight→volume: "${productLabel}" ${before} → ${newQty} ml (÷ ${density} g/ml)`);
    return { ...item, totalQty: newQty, unit: 'ml', measurementType: 'volume' };
  }

  // count <-> volume or volume <-> count: not supported, keep original
  console.log(`[CanonicalCoerce] ⚠️ Unsupported coercion path ${current}→${canonical} for "${productLabel}" — keeping ${before}`);
  return item;
};

/**
 * Determine product type based on category, measurement type, and name patterns
 * @param {String} category - Product category
 * @param {Object} ingredient - Ingredient with measurementType
 * @param {String} productName - Product name for pattern matching
 * @returns {String} Product type: 'unit', 'weight', 'package', or 'volume'
 */
const inferProductType = (category, ingredient, productName = '') => {
  const normalizedCat = normalizeCategory(category || 'Other');
  const name = productName.toLowerCase();
  
  // Volume products - liquids
  if (ingredient?.measurementType === 'volume' || 
      name.includes('milk') || name.includes('leche') || 
      name.includes('oil') || name.includes('aceite') ||
      name.includes('juice') || name.includes('zumo') ||
      name.includes('wine') || name.includes('vino') ||
      name.includes('vinegar') || name.includes('vinagre') ||
      name.includes('cream') || name.includes('nata') ||
      name.includes('broth') || name.includes('caldo') ||
      name.includes('sauce') || name.includes('salsa')) {
    return 'package'; // Liquids come in packages (bottles, cartons)
  }
  
  // Jarred/canned products - sold as packages
  if (name.includes('olive') || name.includes('aceituna') ||
      name.includes('pickle') || name.includes('pepinillo') ||
      name.includes('caper') || name.includes('alcaparra') ||
      name.includes('artichoke') || name.includes('alcachofa')) {
    return 'package'; // These come in jars/cans
  }
  
  // Fresh herbs and spices - sold in packages (jars, containers, bunches)
  if (name.includes('basil') || name.includes('albahaca') ||
      name.includes('dill') || name.includes('eneldo') ||
      name.includes('parsley') || name.includes('perejil') ||
      name.includes('cilantro') || name.includes('coriander') ||
      name.includes('mint') || name.includes('menta') ||
      name.includes('thyme') || name.includes('tomillo') ||
      name.includes('rosemary') || name.includes('romero') ||
      name.includes('oregano') || name.includes('orégano') ||
      name.includes('chive') || name.includes('cebollino') ||
      name.includes('tarragon') || name.includes('estragón') ||
      name.includes('sage') || name.includes('salvia')) {
    return 'package'; // Herbs come in jars/containers/bunches
  }
  
  // Unit products - typically sold by piece
  if (normalizedCat === 'Produce' && ingredient?.measurementType === 'count') {
    return 'unit';
  }
  
  // Eggs, bread, etc. - these typically come in packages of multiple units
  if (name.includes('egg') || name.includes('huevo')) {
    return 'package'; // Eggs come in packages (cartons of 6, 12, etc.)
  }
  
  // Bread (loaves) - sold as packages containing multiple slices
  if (name.includes('bread') || name.includes('pan ') || name.includes(' pan')) {
    return 'package'; // Bread loaves are packages containing ~20 slices
  }
  
  // Bread rolls typically sold by package
  if (name.includes('roll') || name.includes('bollo')) {
    return 'package';
  }
  
  // Produce often sold by weight
  if (normalizedCat === 'Produce') {
    return 'weight';
  }
  
  // Meat typically sold by weight
  if (normalizedCat === 'Meat & Seafood') {
    return 'weight';
  }
  
  // Frozen seafood/fish is typically sold by weight even though it's in Frozen category
  if (normalizedCat === 'Frozen' && 
      (name.includes('fish') || name.includes('pescado') ||
       name.includes('seafood') || name.includes('marisco') ||
       name.includes('salmon') || name.includes('cod') || name.includes('bacalao') ||
       name.includes('shrimp') || name.includes('gamba') || name.includes('gambones') ||
       name.includes('prawn') || name.includes('gambon') ||
       name.includes('monkfish') || name.includes('rape') ||
       name.includes('hake') || name.includes('merluza') ||
       name.includes('squid') || name.includes('calamar') ||
       name.includes('octopus') || name.includes('pulpo') ||
       name.includes('mussel') || name.includes('mejillon') ||
       name.includes('tuna') || name.includes('atún'))) {
    return 'weight';
  }
  
  // Most dairy/condiments/pantry items come in packages
  if (normalizedCat === 'Dairy, Alternatives & Eggs' || 
      normalizedCat === 'Condiments' || 
      normalizedCat === 'Pantry' ||
      normalizedCat === 'Frozen') {
    return 'package';
  }
  
  // Default to package for unknown categories
  return 'package';
};

/**
 * Validate match quality and detect obviously incompatible matches
 * Returns a reason string if match is bad, or null/undefined if match is acceptable
 * 
 * @param {string} ingredientName - Original ingredient name searched
 * @param {string} productName - Matched product name from database
 * @param {string} productCategory - Category of the matched product
 * @returns {string|null} Reason for rejection, or null if match is acceptable
 */
const validateMatchQuality = (ingredientName, productName, productCategory) => {
  // Match validation disabled - matching should be handled by Typesense
  // This function is kept for potential future use but returns null (accept all matches)
  return null;
};

/**
 * Check if a product is a liquid based on name patterns
 * @param {String} productName - Product name
 * @returns {boolean} Whether the product is a liquid
 */
const isLiquidProduct = (productName = '') => {
  const name = productName.toLowerCase();
  return name.includes('milk') || name.includes('leche') ||
         name.includes('oil') || name.includes('aceite') ||
         name.includes('juice') || name.includes('zumo') ||
         name.includes('wine') || name.includes('vino') ||
         name.includes('vinegar') || name.includes('vinagre') ||
         name.includes('cream') || name.includes('nata') ||
         name.includes('broth') || name.includes('caldo') ||
         name.includes('sauce') || name.includes('salsa') ||
         name.includes('stock') || name.includes('yogurt');
};

/**
 * Build a product object with a properly structured sizes array
 * If the Typesense document already has sizes, use them
 * Otherwise, construct a synthetic sizes array from flat document fields
 * @param {Object} doc - Typesense document
 * @param {Object} meta - PRODUCT_METADATA for this product
 * @param {String} category - Product category
 * @param {Object} ingredient - Original ingredient for context
 * @returns {Object} Product with sizes array
 */

/**
 * Normalize a size entry for truly count-based products (eggs, bread loaves).
 * Ensures itemsQty is a sensible carton/loaf size and clears weight so the
 * downstream renderer/calculator uses count-based logic instead of grams.
 *
 * Schema note: when weightUnit is "ud"/"unit"/"units", the product is sold by
 * count and the Typesense `avgWeight` field actually encodes the pack count
 * (e.g. eggs: avgWeight=6 means "6-egg pack"), not grams. In that case we
 * prefer avgWeight over itemsQty since itemsQty in the DB is sometimes a
 * stale/default 12.
 *
 * Returns the original size unchanged for non-count-based products.
 */
const isCountWeightUnit = (wu) => {
  if (!wu) return false;
  const u = String(wu).toLowerCase();
  return u === 'ud' || u === 'uds' || u === 'unit' || u === 'units' || u === 'pcs' || u === 'piece' || u === 'pieces';
};

const resolveCountFromSize = (size, fallback) => {
  const avgCount = parseFloat(size.avgWeight);
  if (isCountWeightUnit(size.weightUnit) && !isNaN(avgCount) && avgCount >= 1) {
    return Math.round(avgCount);
  }
  if (size.itemsQty && size.itemsQty >= 1) return size.itemsQty;
  return fallback;
};

const normalizeCountBasedSize = (size, nameLower) => {
  if (nameLower.includes('egg') || nameLower.includes('huevo')) {
    const count = resolveCountFromSize(size, 12);
    return {
      ...size,
      itemsQty: count,
      weight: null,
    };
  }
  if (nameLower.includes('bread') || nameLower.includes('pan ') || nameLower.includes(' pan')) {
    const count = resolveCountFromSize(size, 20);
    return {
      ...size,
      itemsQty: count,
      weight: null,
    };
  }
  return size;
};

const buildProductWithSizes = (doc, meta, category, ingredient) => {
  // Determine the correct type based on category and ingredient
  const correctType = inferProductType(category, ingredient, doc.displayName || doc.name);
  const nameLower = (doc.displayName || doc.name || '').toLowerCase();
  
  // If document already has a sizes array, validate and potentially fix type mismatches
  if (doc.sizes && Array.isArray(doc.sizes) && doc.sizes.length > 0) {
    const originalType = doc.sizes[0]?.type;
    let needsCorrection = false;
    let correctionReason = '';
    
    // Check if the type needs correction for liquid/volume products
    if (correctType === 'package' && originalType === 'unit' && 
        (ingredient?.measurementType === 'volume' || isLiquidProduct(nameLower))) {
      needsCorrection = true;
      correctionReason = 'liquid/volume product';
    }
    
    // Check if eggs need correction (should be package, not unit)
    // Eggs should always be package type regardless of measurement type
    if (originalType === 'unit' && 
        (nameLower.includes('egg') || nameLower.includes('huevo'))) {
      needsCorrection = true;
      correctionReason = 'count-based package (eggs)';
    }
    
    // Check if bread needs correction (loaves contain multiple slices)
    // Bread should be package type - 1 loaf = ~20 slices
    if (originalType === 'unit' && 
        (nameLower.includes('bread') || nameLower.includes('pan ') || nameLower.includes(' pan'))) {
      needsCorrection = true;
      correctionReason = 'count-based package (bread loaf with slices)';
    }
    
    // Check if jarred/canned products need correction (olives, pickles, capers, artichokes)
    // These should be package type - sold in jars/cans, not by piece
    if (originalType === 'unit' && 
        (nameLower.includes('olive') || nameLower.includes('aceituna') ||
         nameLower.includes('pickle') || nameLower.includes('pepinillo') ||
         nameLower.includes('caper') || nameLower.includes('alcaparra') ||
         nameLower.includes('artichoke') || nameLower.includes('alcachofa'))) {
      needsCorrection = true;
      correctionReason = 'jarred/canned product (sold in containers)';
    }
    
    // Check if weight products are misclassified as unit (meat, seafood, frozen fish)
    if (originalType === 'unit' && correctType === 'weight' &&
        ingredient?.measurementType === 'weight') {
      needsCorrection = true;
      correctionReason = 'weight-based product (meat/seafood)';
    }
    
    // General type mismatch: if correctType differs from original and ingredient measurement aligns
    if (!needsCorrection && originalType === 'unit' && correctType !== 'unit') {
      // Volume requirement with package product
      if (ingredient?.measurementType === 'volume' && correctType === 'package') {
        needsCorrection = true;
        correctionReason = 'volume measurement requires package';
      }
      // Weight requirement with package product (condiments, spices in jars)
      if (ingredient?.measurementType === 'weight' && correctType === 'package') {
        needsCorrection = true;
        correctionReason = 'package product for weight requirement';
      }
    }
    
    if (needsCorrection) {
      console.log(`🔧 Correcting type for ${doc.displayName}: ${originalType} → ${correctType} (${correctionReason})`);
      
      // Clone and correct the sizes array
      const correctedSizes = doc.sizes.map(size => {
        const corrected = {
          ...size,
          type: correctType, // Use the inferred correct type
        };
        
        // For volume products, ensure weight/weightUnit are set
        if (isLiquidProduct(nameLower) || ingredient?.measurementType === 'volume') {
          corrected.weight = size.weight || 500;
          corrected.weightUnit = size.weightUnit || 'ml';
        }
        
        // For weight products, ensure weight/weightUnit are set
        if (correctType === 'weight') {
          corrected.weight = size.weight || 1000; // Default 1kg
          corrected.weightUnit = size.weightUnit || 'g';
          // Clear itemsQty for weight products - they should use weight-based calculations
          corrected.itemsQty = null;
        }
        
        // Determine if this is a TRULY count-based product (eggs, bread only)
        const isTrulyCountBased = nameLower.includes('egg') || nameLower.includes('huevo') ||
                                   nameLower.includes('bread') || nameLower.includes('pan ') ||
                                   nameLower.includes(' pan') || nameLower.includes('roll') ||
                                   nameLower.includes('bollo');
        
        // For eggs, ensure itemsQty is reasonable (eggs come in cartons of 6, 10, 12)
        if (nameLower.includes('egg') || nameLower.includes('huevo')) {
          // If no itemsQty or unreasonably low (< 6), set to default carton size
          if (!size.itemsQty || size.itemsQty < 6) {
            corrected.itemsQty = 12; // Default egg carton size
          }
          // Clear weight for eggs - they should use count-based calculations
          corrected.weight = null;
        }
        
        // For bread loaves, set itemsQty to represent slices per loaf
        if (nameLower.includes('bread') || nameLower.includes('pan ') || nameLower.includes(' pan')) {
          // A loaf of bread has ~20 slices, so 1 loaf satisfies most slice requirements
          if (!size.itemsQty || size.itemsQty < 10) {
            corrected.itemsQty = 20; // Slices per loaf
          }
          // Clear weight for bread - they should use count-based calculations
          corrected.weight = null;
        }
        
        // For all other weight/package products that are NOT truly count-based,
        // clear itemsQty to ensure weight-based calculations are used
        if (!isTrulyCountBased && correctType !== 'weight' && !isLiquidProduct(nameLower)) {
          // This is a weight-based package (cheese, almonds, meat trays, etc.)
          // Clear itemsQty to force weight-based calculation
          if (size.weight && size.weight > 0) {
            corrected.itemsQty = null;
          }
        }
        
        return corrected;
      });
      
      return {
        ...doc,
        sizes: correctedSizes,
        _highlights: doc._highlights,
      };
    }
    
    // Even if no type correction is needed, we should still validate itemsQty
    // to ensure weight-based products don't accidentally use count-based calculations
    const isTrulyCountBased = nameLower.includes('egg') || nameLower.includes('huevo') ||
                               nameLower.includes('bread') || nameLower.includes('pan ') ||
                               nameLower.includes(' pan') || nameLower.includes('roll') ||
                               nameLower.includes('bollo');
    
    // Clean up itemsQty for non-count-based products,
    // and enforce count-based normalization for eggs/bread (carton size, no weight)
    const cleanedSizes = doc.sizes.map(size => {
      if (!isTrulyCountBased && size.weight && size.weight > 0) {
        // This is a weight-based product - clear itemsQty to prevent count-based calculations
        return { ...size, itemsQty: null };
      }
      return normalizeCountBasedSize(size, nameLower);
    });
    
    console.log(`✓ Typesense product ${doc.displayName} already has sizes array (type: ${originalType})`);
    return {
      ...doc,
      sizes: cleanedSizes,
      _highlights: doc._highlights,
    };
  }
  
  // If sizes is a JSON string, parse it
  if (typeof doc.sizes === 'string') {
    try {
      const parsedSizes = JSON.parse(doc.sizes);
      if (Array.isArray(parsedSizes) && parsedSizes.length > 0) {
        console.log(`✓ Parsed sizes JSON for ${doc.displayName}`);
        
        // Clean up itemsQty for non-count-based products
        const isTrulyCountBasedParsed = nameLower.includes('egg') || nameLower.includes('huevo') ||
                                         nameLower.includes('bread') || nameLower.includes('pan ') ||
                                         nameLower.includes(' pan') || nameLower.includes('roll') ||
                                         nameLower.includes('bollo');
        
        const cleanedParsedSizes = parsedSizes.map(size => {
          if (!isTrulyCountBasedParsed && size.weight && size.weight > 0) {
            return { ...size, itemsQty: null };
          }
          return normalizeCountBasedSize(size, nameLower);
        });
        
        return {
          ...doc,
          sizes: cleanedParsedSizes,
          _highlights: doc._highlights,
        };
      }
    } catch (e) {
      console.warn(`Failed to parse sizes JSON for ${doc.displayName}:`, e);
    }
  }
  
  console.log(`🔧 Building synthetic sizes array for Typesense product: ${doc.displayName}`);
  
  // nameLower is already defined at the top of the function
  
  // Infer product type based on category and ingredient measurement
  const productType = inferProductType(category, ingredient, doc.displayName || doc.name);
  
  // Get values from document or use fallbacks
  const normalizedCat = normalizeCategory(category || 'Other');
  
  // Try to get price from document, or estimate based on category
  let price = parseFloat(doc.price) || parseFloat(doc.price_amount);
  if (isNaN(price) || price <= 0) {
    price = CATEGORY_PRICING_DEFAULTS[normalizedCat] || 3.00;
  }
  
  // Determine if this is a count-based package product (eggs, bread)
  const isCountBasedProduct = nameLower.includes('egg') || nameLower.includes('huevo') ||
    nameLower.includes('bread') || nameLower.includes('pan ') || 
    nameLower.includes(' pan') || nameLower.includes('roll') || nameLower.includes('bollo');
  
  // Try to get weight/volume from document
  let weight = parseFloat(doc.weight) || parseFloat(doc.size) || parseFloat(doc.quantity);
  let weightUnit = doc.weightUnit || doc.unit || doc.size_unit;
  
  // SANITY CHECK: Detect and fix likely kg values stored as grams
  // For weight products, if weight is a small decimal (< 10), it's likely in kg not grams
  if (weight && weight > 0 && weight < 10 && productType === 'weight') {
    // Check if it looks like a kg value (typical range: 0.1 to 5 kg)
    if (!weightUnit || weightUnit === 'g') {
      console.warn(`⚠️ buildProductWithSizes: Suspicious small weight ${weight}g for weight product "${doc.displayName}", converting to kg`);
      weight = weight * 1000; // Convert to grams
      weightUnit = 'g';
    }
  }
  
  // Default weights based on product type - but NOT for count-based products
  if ((isNaN(weight) || weight <= 0) && !isCountBasedProduct) {
    if (productType === 'package') {
      // Determine if this is a volume product (liquid)
      const isLiquid = ingredient?.measurementType === 'volume' ||
        nameLower.includes('milk') || nameLower.includes('leche') ||
        nameLower.includes('oil') || nameLower.includes('aceite') ||
        nameLower.includes('juice') || nameLower.includes('zumo') ||
        nameLower.includes('wine') || nameLower.includes('vino') ||
        nameLower.includes('cream') || nameLower.includes('nata') ||
        nameLower.includes('sauce') || nameLower.includes('salsa') ||
        nameLower.includes('vinegar') || nameLower.includes('vinagre') ||
        nameLower.includes('broth') || nameLower.includes('caldo');
      
      if (isLiquid) {
        weight = 1000; // 1L for liquids
        weightUnit = 'ml';
      } else {
        weight = 500; // 500g default package
        weightUnit = 'g';
      }
    } else if (productType === 'weight') {
      weight = 1000; // 1kg default for weight products
      weightUnit = 'g';
    } else {
      weight = 1; // 1 piece for unit products
      weightUnit = 'piece';
    }
  }
  
  // Normalize weight unit
  if (!weightUnit) {
    weightUnit = (productType === 'volume' || (productType === 'package' && ingredient?.measurementType === 'volume'))
      ? 'ml' 
      : (productType === 'unit' ? 'piece' : 'g');
  }
  
  // Get avgWeight for unit products
  let avgWeight = null;
  if (productType === 'unit') {
    avgWeight = getAvgWeightWithFallback({ avgWeight: doc.avgWeight }, doc.displayName || doc.name, category);
  }
  
  // Determine itemsQty for count-based packages (eggs, bread rolls, etc.)
  // ONLY set itemsQty for products that are truly sold by count (eggs, bread), not weight-based packages
  let itemsQty = null;
  // nameLower is already defined at the top of the function
  if (productType === 'package') {
    if (nameLower.includes('egg') || nameLower.includes('huevo')) {
      itemsQty = 12; // Default eggs carton size
    } else if (nameLower.includes('bread') || nameLower.includes('pan ') || nameLower.includes(' pan')) {
      itemsQty = 20; // A loaf of bread has ~20 slices
    } else if (nameLower.includes('roll') || nameLower.includes('bollo')) {
      itemsQty = 6; // Default bread roll package
    }
    // DO NOT set itemsQty for other products - they should use weight-based calculations
  } else if (productType === 'unit') {
    itemsQty = 1;
  }
  
  // Build synthetic size entry
  // For count-based products (eggs, bread), weight should be null to use itemsQty for calculations
  const syntheticSize = {
    name: doc.displayName || doc.name || 'Product',
    image: doc.thumbnail || doc.image || "https://via.placeholder.com/300x300.png?text=Product",
    type: productType,
    price: price,
    weight: (productType === 'unit' || isCountBasedProduct) ? null : weight,
    weightUnit: isCountBasedProduct ? null : weightUnit,
    avgWeight: avgWeight,
    itemsQty: itemsQty,
    description: doc.description || doc.displayName || doc.name,
    rating: parseFloat(doc.rating) || null,
    segment: doc.segment || 'standard'
  };
  
  console.log(`  → Type: ${productType}, Price: ${price}, Weight: ${weight}${weightUnit}, ItemsQty: ${itemsQty}, AvgWeight: ${avgWeight}`);
  
  return {
    ...doc,
    sizes: [syntheticSize],
    _highlights: doc._highlights,
  };
};

// ─── Recipe ingredient scaling helpers ───────────────────────────────────────

/**
 * Format a scaled numeric quantity nicely.
 * - ≥100 → round to integer
 * - ≥10  → 1 decimal place
 * - ≥1   → 1 decimal place
 * - <1   → nearest 1/4 fraction (1/4, 1/2, 3/4)
 */
const formatScaledQtyValue = (value) => {
  if (value <= 0) return '1';
  if (value >= 100) return String(Math.round(value));
  if (value >= 10)  return String(parseFloat(value.toFixed(1)));
  if (value >= 1)   return String(parseFloat(value.toFixed(1)));
  // Express as fraction rounded to nearest 1/4
  const quarters = Math.max(1, Math.round(value * 4));
  const whole = Math.floor(quarters / 4);
  const rem = quarters % 4;
  const fractions = { 1: '1/4', 2: '1/2', 3: '3/4' };
  if (rem === 0) return String(whole);
  return whole > 0 ? `${whole} ${fractions[rem]}` : fractions[rem];
};

/**
 * Scale a human-readable qty string by a ratio.
 * Examples: "300 g" × 0.85 → "255 g", "2 cups" × 0.85 → "1.7 cups"
 * Returns original string if it can't be parsed.
 */
const scaleQtyString = (qtyStr, ratio) => {
  if (!qtyStr || ratio >= 1) return qtyStr;
  const str = String(qtyStr).trim();

  // Unicode fraction pre-normalisation
  const UNICODE_FRACTIONS = { '½': '1/2', '⅓': '1/3', '⅔': '2/3', '¼': '1/4', '¾': '3/4', '⅛': '1/8' };
  let normalized = str;
  for (const [uf, ascii] of Object.entries(UNICODE_FRACTIONS)) {
    normalized = normalized.replace(uf, ` ${ascii}`).replace(/\s{2,}/g, ' ').trim();
  }

  // Mixed number: "1 1/2 tbsp"
  const mixedMatch = normalized.match(/^(\d+)\s+(\d+)\/(\d+)(.*)/);
  // Fraction: "1/2 cup"
  const fractionMatch = !mixedMatch && normalized.match(/^(\d+)\/(\d+)(.*)/);
  // Plain number: "300 g"
  const numberMatch = !mixedMatch && !fractionMatch && normalized.match(/^([\d.]+)(.*)/);

  let value, rest;
  if (mixedMatch) {
    value = parseInt(mixedMatch[1]) + parseInt(mixedMatch[2]) / parseInt(mixedMatch[3]);
    rest = mixedMatch[4];
  } else if (fractionMatch) {
    value = parseInt(fractionMatch[1]) / parseInt(fractionMatch[2]);
    rest = fractionMatch[3];
  } else if (numberMatch) {
    value = parseFloat(numberMatch[1]);
    rest = numberMatch[2];
  } else {
    return qtyStr; // unparseable – leave unchanged
  }

  const scaled = value * ratio;
  return `${formatScaledQtyValue(scaled)}${rest}`.trim();
};

/**
 * Apply ingredient quantity scaling adjustments to the meal plan.
 * @param {Array} mealPlan - Original meal plan array
 * @param {Array} adjustments - [{ title, ingredientIndex, ratio }]
 * @returns {Array} New meal plan with scaled ingredient qtys
 */
const applyIngredientScaling = (mealPlan, adjustments) => {
  if (!adjustments || adjustments.length === 0) return mealPlan;

  // Build map: { recipeTitle: { ingredientIndex: ratio } }
  const adjustMap = {};
  adjustments.forEach(({ title, ingredientIndex, ratio }) => {
    if (!adjustMap[title]) adjustMap[title] = {};
    const existing = adjustMap[title][ingredientIndex];
    // If same ingredient gets multiple adjustments, take the minimum (most conservative)
    adjustMap[title][ingredientIndex] = existing !== undefined
      ? Math.min(existing, ratio)
      : ratio;
  });

  return mealPlan.map(recipe => {
    const recipeAdjustments = adjustMap[recipe.title];
    if (!recipeAdjustments || !Array.isArray(recipe.ingredients)) return recipe;

    let changed = false;
    const updatedIngredients = recipe.ingredients.map((ingredient, idx) => {
      const ratio = recipeAdjustments[idx];
      if (ratio === undefined || ratio >= 1) return ingredient;
      const newQty = scaleQtyString(ingredient.qty, ratio);
      if (newQty === ingredient.qty) return ingredient;
      changed = true;
      return { ...ingredient, qty: newQty };
    });

    return changed ? { ...recipe, ingredients: updatedIngredients } : recipe;
  });
};

/**
 * Process a meal plan into a grocery list using metric measurements
 * @param {Array} mealPlan - The meal plan with recipes
 * @param {Array} countryProducts - Product database for the country
 * @param {Object} ctx - Context object with country, currency, etc.
 * @param {Number} numberOfPeople - Number of people
 * @param {Function} onProgress - Progress callback
 * @param {Object} options - Processing options (e.g., skipAIFallback)
 * @returns {Object} Processed grocery list
 */
export const processGroceryListMetric = async (
  mealPlan,
  countryProducts,
  ctx,
  numberOfPeople,
  onProgress = null,
  options = {}
) => {
  try {
    if (onProgress) {
      onProgress({
        step: "metric-processing",
        progress: 30,
        message: "Consolidating ingredients (metric)...",
      });
    }
    // 1. Consolidate ingredients
    let consolidatedIngredients = consolidateIngredientsMetric(
      mealPlan,
      numberOfPeople
    );
    
    // TESTFLOW: Log consolidated ingredients to debug missing items
    console.log('[TESTFLOW] Consolidated ingredients:', consolidatedIngredients.map(i => ({
      name: i.name,
      originalName: i.originalName,
      totalQty: i.totalQty,
      unit: i.unit,
      measurementType: i.measurementType,
      usedIn: i.usedIn
    })));
    
    /*
    // 2. Translate ingredient names if market language is not English
    const countryPrefs = countryFoodPreferences[ctx.country];
    let marketLanguage = countryPrefs?.marketLanguage;

    // Language code to name mapping (fallback if marketLanguage is just a code string)
    const LANGUAGE_NAMES = {
      es: "Spanish",
      de: "German", 
      fr: "French",
      it: "Italian",
      pt: "Portuguese",
      nl: "Dutch",
      pl: "Polish",
      ru: "Russian",
      zh: "Chinese",
      ja: "Japanese",
      ko: "Korean",
      ca: "Catalan",
      ro: "Romanian",
      uk: "Ukrainian"
    };

    // Normalize marketLanguage to always be [code, name] array
    if (typeof marketLanguage === "string") {
      const langName = LANGUAGE_NAMES[marketLanguage] || marketLanguage;
      marketLanguage = [marketLanguage, langName];
    }

    if (marketLanguage && marketLanguage[0] !== "en") {
      if (onProgress) {
        onProgress({
          step: "translating",
          progress: 40,
          message: `Translating ingredients to ${marketLanguage[1]}...`,
        });
      }

      consolidatedIngredients = await translateIngredientsToMarketLanguage(
        consolidatedIngredients,
        marketLanguage
      );
    }*/

    if (onProgress) {
      onProgress({
        step: "matching",
        progress: 50,
        message: "Matching ingredients to products...",
      });
    }

    // 3. Match ingredients to products
    const matchedProducts = await matchIngredientsToProductsMetric(
      consolidatedIngredients,
      countryProducts,
      ctx.country // Pass country for analytics tracking
    );

    console.log(" v v v v v v v v v v v v v v ", matchedProducts);

    // 3. AI fallback for unmatched ingredients (before quantity calculations)
    const unmatchedItems = matchedProducts.filter((item) => item.unmatched);
    let allProducts = [...matchedProducts];

    // TESTFLOW: Log unmatched items that need AI fallback
    console.log('[TESTFLOW] Unmatched items for AI fallback:', unmatchedItems.map(i => ({
      name: i.name,
      originalName: i.originalName,
      totalQty: i.totalQty,
      unit: i.unit
    })));

    let aiResults;

    if (unmatchedItems.length > 0 && !options.skipAIFallback) {
      if (onProgress) {
        onProgress({
          step: "ai_fallback",
          progress: 60,
          message: `Processing ${unmatchedItems.length} unmatched ingredients with AI...`,
        });
      }

      aiResults = await processUnmatchedIngredientsWithAI(
        unmatchedItems,
        ctx.country
      );

      // TESTFLOW: Log AI results
      console.log('[TESTFLOW] AI fallback results:', aiResults?.map(r => ({
        name: r.name,
        productName: r.productName,
        price: r.price,
        unmatched: r.unmatched
      })));

      // Replace unmatched items with AI results
      allProducts = matchedProducts.filter((item) => !item.unmatched);
      allProducts.push(...aiResults);
    } else if (options.skipAIFallback) {
      // Skip AI processing and only use matched products
      allProducts = matchedProducts.filter((item) => !item.unmatched);
      if (onProgress && unmatchedItems.length > 0) {
        onProgress({
          step: "skipping-ai",
          progress: 60,
          message: `Skipping AI processing for ${unmatchedItems.length} unmatched ingredients...`,
        });
      }
    }

    if (onProgress) {
      onProgress({
        step: "calculating",
        progress: 70,
        message: "Calculating quantities and prices (metric)...",
      });
    }

    // 3.5 POST-MATCH CONSOLIDATION: Merge items that matched to the same product
    // This handles cases like "preserved lemon" and "lemon" both matching to "Lemon"
    const postMatchConsolidated = consolidateByProductId(allProducts);
    
    console.log('[TESTFLOW] Post-match consolidated:', postMatchConsolidated.map(p => ({
      productId: p.productId,
      productName: p.productName,
      totalQty: p.totalQty,
      originalIngredients: p.allOriginals
    })));

    // Notify callers that Typesense matching is complete so recipe details can start
    // with real product data. Group the flat postMatchConsolidated into the
    // [{category, items:[...]}] format expected by buildIngredientContext.
    if (onProgress) {
      const grouped = {};
      postMatchConsolidated.forEach(item => {
        const cat = normalizeCategory(item.category || 'Other');
        if (!grouped[cat]) grouped[cat] = { category: cat, items: [] };
        grouped[cat].items.push(item);
      });
      onProgress({
        step: 'typesense_matched',
        progress: 65,
        message: 'Products matched — preparing recipe details...',
        matchedProducts: Object.values(grouped),
      });
    }

    // 4. Calculate purchase quantities (unified for both database and AI products)
    const calculatedProducts = calculatePurchaseQuantitiesMetric(
      postMatchConsolidated,
      countryProducts,
      ctx.currency,
      ctx.userSegment || "standard" // Default to standard segment if not specified
    );

    console.log(
      "\n\n\n mealPlan: \n\n" +
        JSON.stringify(mealPlan, null, 2) +
        "\n\n\n consolidatedIngredients: \n\n" +
        JSON.stringify(consolidatedIngredients, null, 2) +
        "\n\n\n matchedProducts: \n\n" +
        JSON.stringify(matchedProducts, null, 2) +
        "\n\n\n unmatchedItems: \n\n" +
        JSON.stringify(unmatchedItems, null, 2) +
        "\n\n\n aiProducts: \n\n" +
        JSON.stringify(aiResults, null, 2) +
        "\n\n\n calculatedProducts: \n\n" +
        JSON.stringify(calculatedProducts, null, 2)
    );

    // 5. Finalize grocery list
    if (onProgress) {
      onProgress({
        step: "finalizing",
        progress: 90,
        message: "Finalizing grocery list...",
      });
    }

    // Removed: const categorizedGroceryList = categorizeProductsMetric(calculatedProducts);
    const totalCost = calculateTotalCostMetric(
      calculatedProducts,
      ctx.currency
    );
    const leftovers = calculateLeftoversMetric(calculatedProducts);
    const maxStorageTime = determineMaxStorageTimeMetric(calculatedProducts);

    // ── Scale recipe ingredient quantities back proportionally when smartPackagesNeeded
    // rounded down (e.g., needed 600g but buying 510g = 1 pack, ratio = 0.85)
    const scalingAdjustments = [];
    calculatedProducts.forEach(item => {
      const ratio = item.quantityRatio;
      if (ratio !== undefined && ratio < 1 && Array.isArray(item.recipeContributions)) {
        item.recipeContributions.forEach(({ title, ingredientIndex }) => {
          scalingAdjustments.push({ title, ingredientIndex, ratio });
        });
      }
    });
    const updatedMealPlan = applyIngredientScaling(mealPlan, scalingAdjustments);
    if (scalingAdjustments.length > 0) {
      console.log(`[processGroceryListMetric] Applied ingredient scaling to ${scalingAdjustments.length} ingredient(s) across recipes`);
    }

    // Group products by normalized category directly from calculatedProducts
    // Build a name -> { form, productHint } map from the raw meal plan so the
    // deterministic storage classifier can use the `form` signal (which is
    // dropped during consolidation).
    const ingredientFormMap = buildIngredientFormMap(mealPlan);

    // Build a recipe-title -> required freshness (days) map so perishable ingredients
    // (fish, raw meat) can be pushed to the FREEZER when the dish(es) they belong to
    // must stay fresh longer than they keep in the fridge (dish freshness drives storage).
    const recipeFreshnessByTitle = new Map();
    if (Array.isArray(mealPlan)) {
      for (const r of mealPlan) {
        if (!r || !r.title) continue;
        const d = parseInt(r.maxStorageTime ?? r.freshness, 10);
        if (Number.isFinite(d) && d > 0) recipeFreshnessByTitle.set(r.title, d);
      }
    }
    const computeRequiredFreshness = (item) => {
      const titles = Array.isArray(item?.usedIn)
        ? item.usedIn
        : (item?.recipeContributions || []).map((c) => c.title);
      let maxDays = 0;
      for (const tt of titles) {
        const d = recipeFreshnessByTitle.get(tt);
        if (Number.isFinite(d) && d > maxDays) maxDays = d;
      }
      return maxDays > 0 ? maxDays : undefined;
    };

    const grouped = {};
    calculatedProducts.forEach((item) => {
      // Changed from categorizedGroceryList
      const cat = normalizeCategory(item.category || "Other");
      if (!grouped[cat]) grouped[cat] = { category: cat, items: [] };

      // Deterministic storage classification (fridge/freezer/pantry + shelf-life).
      // `form` is the primary signal; falls back to keywords, fish detection,
      // curated name overrides, PRODUCT_METADATA and category. Always sets a value.
      const formInfo = resolveIngredientForm(ingredientFormMap, item);
      const classifyInput = {
        ...item,
        form: item.form || formInfo.form || null,
        productHint: item.productHint || formInfo.productHint || null,
      };
      const classified = classifyItemStorage(classifyInput, {
        recipeStorageHint: item.storage,
        requiredFreshnessDays: computeRequiredFreshness(item),
      });
      const enrichedItem = {
        ...item,
        form: classifyInput.form,
        productHint: classifyInput.productHint,
        storage: classified.storage,
        shelfLifeDays: classified.shelfLifeDays,
        longLasting: classified.longLasting,
      };

      grouped[cat].items.push(enrichedItem);
    });

    if (onProgress) {
      onProgress({
        step: "complete",
        progress: 100,
        message: "Grocery list processing complete!",
      });
    }

    // console.log ('- - - - GROUPED - - - - ', grouped);

    return {
      // Removed products from intermediate result
      totalCost,
      leftovers,
      maxStorageTime,
      products: Object.values(grouped), // Return the correctly grouped products
      updatedMealPlan,                  // Meal plan with proportionally scaled ingredient qtys
    };
  } catch (error) {
    console.error("Error in processGroceryListMetric:", error);
    if (onProgress) {
      onProgress({ step: "error", message: error.message });
    }
    return { error: error.message };
  }
};

/**
 * Translate ingredient names to the market language using OpenAI
 * @param {Array} ingredients - Array of consolidated ingredients
 * @param {Array} marketLanguage - Market language array [code, name] (e.g., ["es", "Spanish"])
 * @returns {Promise<Array>} Ingredients with translated names
 */
const translateIngredientsToMarketLanguage = async (
  ingredients,
  marketLanguage
) => {
  const languageName = Array.isArray(marketLanguage) ? marketLanguage[1] : marketLanguage;

  try {
    const ingredientNames = ingredients.map((ing) => ing.name);

    const prompt = `Translate the following ingredient names from English to ${languageName}. 
Use common supermarket product names that shoppers would recognize in ${languageName}-speaking countries.
Return ONLY a JSON array of translated names in the same order as the input.

Input ingredients:
${JSON.stringify(ingredientNames)}

Return format: ["translated1", "translated2", ...]`;

    const result = await fetchFromBackend('/grocery-fallback', {
      model: MODEL_CONFIG.ingredients,
      systemPrompt: `You are a grocery shopping assistant that translates ingredient names to ${languageName} using common supermarket terminology.`,
      userPrompt: prompt,
    });

    const content = result.text;

    // Parse the JSON response
    let translatedNames;
    try {
      // Handle potential markdown code blocks
      let jsonContent = content;
      if (jsonContent.startsWith("```json")) {
        jsonContent = jsonContent.slice(7);
      } else if (jsonContent.startsWith("```")) {
        jsonContent = jsonContent.slice(3);
      }
      if (jsonContent.endsWith("```")) {
        jsonContent = jsonContent.slice(0, -3);
      }
      translatedNames = JSON.parse(jsonContent.trim());
    } catch (parseError) {
      console.error(
        "Failed to parse translation response:",
        parseError,
        content
      );
      return ingredients;
    }

    // Map translated names back to ingredients
    if (
      Array.isArray(translatedNames) &&
      translatedNames.length === ingredients.length
    ) {
      ingredients = ingredients.map((ing, index) => ({
        ...ing,
        originalEnglishName: ing.name,
        name: translatedNames[index] || ing.name,
      }));
      console.log("ingredients", JSON.stringify(ingredients));
      return ingredients;
      
    }

    console.warn("Translation response length mismatch, using original names");
    return ingredients;
  } catch (error) {
    console.error("Error translating ingredients:", error);
    return ingredients;
  }
};


/**
 * Consolidate ingredients from recipes using metric system
 * @param {Array} mealPlan - Array of recipes
 * @param {Number} numberOfPeople - Number of people to cook for
 * @returns {Array} Consolidated ingredients
 */
export const consolidateIngredientsMetric = (mealPlan, numberOfPeople) => {
  const ingredientsMap = {};
  
  // NOTE: numberOfPeople is passed but NOT used for multiplication.
  // The AI prompt already calculates quantities for the target number of people.
  // We keep the parameter for logging/debugging purposes only.
  
  // Ingredients to SKIP - these are pantry staples or tap water, not grocery items
  const SKIP_INGREDIENTS = [
    'water',        // Tap water for recipes (dough, boiling, etc.)
    'tap water',
    'ice',
    'ice cube',
    'ice cubes',
  ];
  
  console.log(`[ConsolidateIngredients] Processing (numberOfPeople from prompt: ${numberOfPeople}, NOT used for multiplication)`);

  mealPlan.forEach((recipe) => {
    if (!Array.isArray(recipe.ingredients)) return;

    recipe.ingredients.forEach((ingredient, ingredientIndex) => {
      const originalName = ingredient.product || "";
      const normalizedName = normalizeIngredientName(originalName);
      
      // SKIP tap water and other non-purchasable ingredients
      if (SKIP_INGREDIENTS.includes(normalizedName)) {
        console.log(`[ConsolidateIngredients] SKIPPING non-purchasable: "${originalName}" (normalized: ${normalizedName})`);
        return; // Skip this ingredient
      }

      // Parse quantity using metric system
      let qtyValue = 1;
      let qtyUnit = "piece";

      if (ingredient.qty) {
        // Normalize Unicode fractions to ASCII before parsing
        // LLMs sometimes emit ½, ¼, ¾ etc. instead of "1/2", "1/4", "3/4"
        const UNICODE_FRACTIONS = { '½': '1/2', '⅓': '1/3', '⅔': '2/3', '¼': '1/4', '¾': '3/4', '⅕': '1/5', '⅛': '1/8' };
        let rawQty = String(ingredient.qty).toLowerCase().trim();
        for (const [uf, ascii] of Object.entries(UNICODE_FRACTIONS)) {
          if (rawQty.includes(uf)) {
            // "1½" → "1 1/2", "½" → "1/2"
            rawQty = rawQty.replace(uf, ` ${ascii}`).replace(/^\s+/, '').replace(/\s{2,}/g, ' ');
          }
        }
        const qtyStr = rawQty;
        
        // Map common unit variations
        const unitMap = {
          'tbsp': 'tbsp',
          'tablespoon': 'tbsp',
          'tablespoons': 'tbsp',
          'tsp': 'tsp',
          'teaspoon': 'tsp',
          'teaspoons': 'tsp',
          'clove': 'clove',
          'cloves': 'clove',
          'unit': 'piece',
          'units': 'piece',
          'bunch': 'bunch',
          'bunches': 'bunch',
          'slice': 'slice',
          'slices': 'slice',
          'g': 'g',
          'gram': 'g',
          'grams': 'g',
          'kg': 'kg',
          'ml': 'ml',
          'l': 'l',
          'piece': 'piece',
          'pieces': 'piece',
          'pinch': 'pinch',
        };
        
        // Normalize multi-word unit descriptors before parsing
        // "small pinch" → "pinch", "generous pinch" → "pinch", "large pinch" → "pinch"
        const qtyStrNormalized = qtyStr
          .replace(/\b(small|large|generous|big)\s+pinch\b/gi, 'pinch');
        // Use normalized string from here on
        const qtyStrFinal = qtyStrNormalized;
        
        // Handle "to taste" BEFORE regex parsing
        // "to taste" doesn't have a numeric value, so we use a small default
        if (qtyStrFinal === 'to taste' || qtyStrFinal.includes('to taste')) {
          qtyValue = 1;
          qtyUnit = 'to taste';
          console.log(`[ConsolidateIngredients] "to taste" detected for ${normalizedName}`);
        } else {
        // Try fraction parsing first: handles "1/2 tsp", "3/4 cup", "1 1/2 tbsp"
        // Mixed numbers: "1 1/2 tsp"
        const mixedMatch = qtyStrFinal.match(/^(\d+)\s+(\d+)\/(\d+)\s*([a-zA-Z]*)/);
        // Simple fractions: "1/2 tsp", "3/4 cup"
        const fractionMatch = !mixedMatch && qtyStrFinal.match(/^(\d+)\/(\d+)\s*([a-zA-Z]*)/);
        
        if (mixedMatch) {
          const whole = parseFloat(mixedMatch[1]);
          const num = parseFloat(mixedMatch[2]);
          const den = parseFloat(mixedMatch[3]);
          qtyValue = den > 0 ? whole + num / den : whole;
          const rawUnit = (mixedMatch[4] || "").trim().toLowerCase();
          qtyUnit = unitMap[rawUnit] || rawUnit || "piece";
          console.log(`[ConsolidateIngredients] Mixed fraction: "${qtyStrFinal}" → ${qtyValue} ${qtyUnit}`);
        } else if (fractionMatch) {
          const num = parseFloat(fractionMatch[1]);
          const den = parseFloat(fractionMatch[2]);
          qtyValue = den > 0 ? num / den : 1;
          const rawUnit = (fractionMatch[3] || "").trim().toLowerCase();
          qtyUnit = unitMap[rawUnit] || rawUnit || "piece";
          console.log(`[ConsolidateIngredients] Fraction: "${qtyStrFinal}" → ${qtyValue} ${qtyUnit}`);
        } else {
        // Standard regex: capture number and unit, ignore descriptors like "grated", "minced", "diced", "sliced", "chopped"
        const match = qtyStrFinal.match(/^([\d.]+)\s*([a-zA-Z]+)?/);
        if (match) {
          qtyValue = parseFloat(match[1]) || 1;
          // Get just the unit word, not descriptors
          let rawUnit = (match[2] || "").trim().toLowerCase();
          
          qtyUnit = unitMap[rawUnit] || rawUnit || "piece";
        }
        }
        } // Close the else block for non "to taste" quantities
      }

      // Convert pinch to tsp equivalent before normalization
      // 1 pinch ≈ 1/16 tsp (standard culinary measure)
      if (qtyUnit === 'pinch') {
        const originalPinchQty = qtyValue;
        qtyValue = qtyValue * 0.0625; // 1/16 tsp per pinch
        qtyUnit = 'tsp';
        console.log(`[ConsolidateIngredients] Pinch conversion: ${originalPinchQty} pinch → ${qtyValue} tsp for ${normalizedName}`);
      }

      // NOTE: We do NOT multiply by numberOfPeople here!
      // The AI prompt already calculates quantities for the target number of people.
      // Multiplying again would cause double-counting (e.g., 6 eggs × 6 people = 36 eggs instead of 6)

      // Use unified part-to-whole system to normalize units
      // This handles garlic cloves, bread slices, basil leaves, etc.
      const partNormalized = normalizePartUnit(normalizedName, qtyUnit, qtyValue);
      if (partNormalized.config) {
        qtyUnit = partNormalized.normalizedUnit;
        console.log(`[PartToWhole] ${normalizedName}: ${qtyValue} ${qtyUnit} (isPart: ${partNormalized.isPart})`);
      }

      // Handle "to taste" quantities
      if (qtyUnit === "to taste") {
        // For "to taste" items like salt, pepper, use a small default amount per recipe
        // This allows consolidation across recipes (e.g., 8 recipes × 1g = 8g total)
        const productMeta = PRODUCT_METADATA[normalizedName];
        if (productMeta && productMeta.avgWeight) {
          qtyValue = productMeta.avgWeight.metric || 1;
          qtyUnit = productMeta.marketWeight?.metric || "g";
        } else {
          // Default: 1g per "to taste" mention for common seasonings
          qtyValue = 1;
          qtyUnit = "g";
          console.log(`[ConsolidateIngredients] "to taste" → defaulting to 1g for ${normalizedName}`);
        }
      }
      // NOTE: Volume/weight pre-conversion REMOVED.
      // normalizeToMetricBase() already handles tsp→ml, kg→g, etc.
      // Pre-converting here was bypassing dry ingredient detection in normalizeToMetricBase,
      // causing spices/salt in tsp/tbsp to end up as ml instead of being converted to grams via density.

      // Normalize to metric base units
      const normalizedQty = normalizeToMetricBase(
        qtyValue,
        qtyUnit,
        normalizedName
      );
      
      // Get consolidation unit using unified system
      const consolidationUnit = getConsolidationUnit(normalizedName, normalizedQty.unit);
      
      // Also update normalizedQty.unit if it's a part product
      if (partNormalized.config && partNormalized.isPart) {
        normalizedQty.unit = consolidationUnit;
      }
      
      const key = `${normalizedName}_${normalizedQty.measurementType}_${consolidationUnit}`;
      
      if (!ingredientsMap[key]) {
        ingredientsMap[key] = {
          name: normalizedName,
          originalName,
          allOriginals: [originalName],
          totalQty: normalizedQty.value,
          unit: normalizedQty.unit,
          measurementType: normalizedQty.measurementType,
          originalUnit: normalizedQty.originalUnit,
          usedIn: [recipe.title],
          recipeContributions: [{ title: recipe.title, ingredientIndex, qty: normalizedQty.value, unit: normalizedQty.unit }],
        };
        console.log(`[Consolidate] NEW: ${key} = ${normalizedQty.value} ${normalizedQty.unit} (from ${recipe.title})`);
      } else {
        const prevQty = ingredientsMap[key].totalQty;
        ingredientsMap[key].totalQty += normalizedQty.value;
        console.log(`[Consolidate] ADD: ${key}: ${prevQty} + ${normalizedQty.value} = ${ingredientsMap[key].totalQty} ${normalizedQty.unit} (from ${recipe.title})`);
        if (!ingredientsMap[key].usedIn.includes(recipe.title)) {
          ingredientsMap[key].usedIn.push(recipe.title);
        }
        ingredientsMap[key].recipeContributions.push({ title: recipe.title, ingredientIndex, qty: normalizedQty.value, unit: normalizedQty.unit });
        if (!ingredientsMap[key].allOriginals.includes(originalName)) {
          ingredientsMap[key].allOriginals.push(originalName);
        }
      }
    });
  });

  // Post-consolidation conversions using unified system
  const consolidatedList = Object.values(ingredientsMap);
  
  // Log final consolidation results
  console.log('[Consolidate] FINAL RESULTS:', consolidatedList.map(i => ({
    name: i.name,
    totalQty: i.totalQty,
    unit: i.unit,
    usedIn: i.usedIn.length
  })));
  
  // Apply part-to-whole conversions AFTER consolidation
  const convertedList = consolidatedList.map(item => {
    const converted = convertPartsToWholes(item);
    return converted;
  });
  
  return convertedList;
};

/**
 * POST-MATCH CONSOLIDATION: Merge items that matched to the same product
 * This handles cases like:
 * - "preserved lemon" and "lemon" both matching to "Lemon" product
 * - "mixed greens" and "mixed mushrooms" both matching to "Frozen Seafood Mix"
 * 
 * SMART MERGE: When items have different measurement types (weight vs count),
 * we can merge them by converting count to weight using avgWeight.
 * Example: "2 carrots" (count) + "150g carrots" (weight) → merge to weight using avgWeight
 * 
 * @param {Array} matchedProducts - Products after Typesense matching
 * @returns {Array} Consolidated products with combined quantities
 */
export const consolidateByProductId = (rawMatchedProducts) => {
  // ----------------------------------------------------------------------
  // Phase B6: canonical-unit coercion BEFORE map keying.
  // Forces every item with a matched Typesense product into a single
  // canonical measurementType (count | weight | volume) per product so
  // duplicates with different units (e.g. pepper 2g + pepper 50ml, garlic
  // 2 cloves + garlic 50g) collapse onto the same map key naturally.
  // The smart count↔weight merge below remains as a fallback for items
  // without a Typesense match or where coercion was impossible.
  // ----------------------------------------------------------------------
  const matchedProducts = rawMatchedProducts.map(coerceToCanonicalUnit);
  const productMap = {};
  
  console.log('[PostMatchConsolidate] Input items (post-canonical-coercion):', matchedProducts.map(p => ({
    name: p.name,
    productId: p.productId,
    productName: p.productName,
    totalQty: p.totalQty,
    unit: p.unit,
    measurementType: p.measurementType
  })));

  /**
   * Convert count to weight using avgWeight (fallback path only — used by
   * the legacy smart-merge below for items that bypassed canonical coercion)
   */
  const convertCountToWeight = (qty, avgWeight) => {
    if (!avgWeight || avgWeight <= 0) return null;
    return Math.round(qty * avgWeight);
  };

  // When a count-based product is folded into a weight-based product, its
  // recipeContributions[].qty are in piece units while the survivor's are in
  // grams. Rescale them by the same factor used for totalQty so per-recipe
  // cost shares (computed downstream from contribution qtys) stay consistent.
  const rescaleContributions = (contributions, fromTotal, toTotal, toUnit) => {
    if (!Array.isArray(contributions)) return [];
    const factor = fromTotal > 0 ? toTotal / fromTotal : 1;
    return contributions.map(c => ({
      ...c,
      qty: typeof c.qty === 'number' ? c.qty * factor : c.qty,
      unit: toUnit,
    }));
  };
  
  matchedProducts.forEach(item => {
    const baseKey = item.productId || `unmatched_${item.name}`;
    const measurementType = item.measurementType || 'unknown';
    
    console.log(`[PostMatchConsolidate] Processing: ${item.name} (${item.totalQty} ${item.unit}, type: ${measurementType}) → baseKey: ${baseKey}`);
    
    // Check if we already have this product with a DIFFERENT measurement type
    const existingWeightKey = `${baseKey}_weight`;
    const existingCountKey = `${baseKey}_count`;
    const hasExistingWeight = productMap[existingWeightKey];
    const hasExistingCount = productMap[existingCountKey];
    
    // SMART MERGE (fallback after canonical-unit coercion above):
    // most duplicate count/weight pairs will already have been collapsed by
    // `coerceToCanonicalUnit`. This branch still catches cases where coercion
    // was impossible (no avgWeight, no Typesense match, etc.).
    if (measurementType === 'count' && hasExistingWeight) {
      // We have weight entry, try to convert this count to weight and merge
      const avgWeight = getProductAvgWeight(item);
      if (avgWeight) {
        const convertedWeight = convertCountToWeight(item.totalQty, avgWeight);
        console.log(`[PostMatchConsolidate] 🔄 Converting count→weight: ${item.totalQty} pieces × ${avgWeight}g = ${convertedWeight}g`);
        
        productMap[existingWeightKey].totalQty += convertedWeight;
        productMap[existingWeightKey].mergedFrom.push(`${item.name} (${item.totalQty} pcs → ${convertedWeight}g)`);
        
        // Merge metadata
        if (item.usedIn) {
          item.usedIn.forEach(recipe => {
            if (!productMap[existingWeightKey].usedIn.includes(recipe)) {
              productMap[existingWeightKey].usedIn.push(recipe);
            }
          });
        }
        if (item.recipeContributions) {
          productMap[existingWeightKey].recipeContributions = [
            ...(productMap[existingWeightKey].recipeContributions || []),
            ...rescaleContributions(item.recipeContributions, item.totalQty, convertedWeight, 'g')
          ];
        }
        return; // Done, skip normal processing
      }
    }
    
    if (measurementType === 'weight' && hasExistingCount) {
      // We have count entry, convert existing count to weight and merge into new weight entry
      const existingItem = productMap[existingCountKey];
      const avgWeight = getProductAvgWeight(existingItem) || getProductAvgWeight(item);
      
      if (avgWeight) {
        const convertedWeight = convertCountToWeight(existingItem.totalQty, avgWeight);
        console.log(`[PostMatchConsolidate] 🔄 Converting existing count→weight: ${existingItem.totalQty} pieces × ${avgWeight}g = ${convertedWeight}g`);
        
        // Create new weight entry with merged totals
        productMap[existingWeightKey] = {
          ...item,
          totalQty: item.totalQty + convertedWeight,
          allOriginals: [...(existingItem.allOriginals || []), ...(item.allOriginals || [item.originalName || item.name])],
          usedIn: [...new Set([...(existingItem.usedIn || []), ...(item.usedIn || [])])],
          recipeContributions: [
            ...rescaleContributions(existingItem.recipeContributions || [], existingItem.totalQty, convertedWeight, 'g'),
            ...(item.recipeContributions || [])
          ],
          mergedFrom: [`${existingItem.name} (${existingItem.totalQty} pcs → ${convertedWeight}g)`, item.name]
        };
        
        // Remove the old count entry
        delete productMap[existingCountKey];
        console.log(`[PostMatchConsolidate] ✅ Merged into weight: ${productMap[existingWeightKey].totalQty}g`);
        return; // Done
      }
    }
    
    // Standard processing - use measurementType in key
    const key = `${baseKey}_${measurementType}`;
    
    if (!productMap[key]) {
      productMap[key] = {
        ...item,
        allOriginals: item.allOriginals || [item.originalName || item.name],
        usedIn: item.usedIn || [],
        mergedFrom: [item.name]
      };
    } else {
      // Same measurementType - simple merge
      const existingUnit = productMap[key].unit;
      const newUnit = item.unit;
      
      // List of count-based units that should be considered compatible
      const COUNT_UNITS = ['piece', 'pieces', 'slice', 'slices', 'loaf', 'loaves', 'unit', 'units', 'whole'];
      
      // Check unit compatibility
      const unitsCompatible = existingUnit === newUnit || 
        (existingUnit === 'g' && newUnit === 'g') ||
        (existingUnit === 'ml' && newUnit === 'ml') ||
        (existingUnit === 'piece' && newUnit === 'piece') ||
        // All count-based units should be compatible for merging
        (COUNT_UNITS.includes(existingUnit) && COUNT_UNITS.includes(newUnit));
      
      if (!unitsCompatible) {
        console.warn(`[PostMatchConsolidate] ⚠️ Unit mismatch within same type: ${existingUnit} vs ${newUnit}`);
        const altKey = `${key}_${newUnit}`;
        productMap[altKey] = {
          ...item,
          allOriginals: item.allOriginals || [item.originalName || item.name],
          usedIn: item.usedIn || [],
          mergedFrom: [item.name]
        };
        return;
      }
      
      // Merge quantities
      productMap[key].totalQty += item.totalQty || 0;
      
      // Merge metadata
      if (item.originalName && !productMap[key].allOriginals.includes(item.originalName)) {
        productMap[key].allOriginals.push(item.originalName);
      }
      if (item.allOriginals) {
        item.allOriginals.forEach(orig => {
          if (!productMap[key].allOriginals.includes(orig)) {
            productMap[key].allOriginals.push(orig);
          }
        });
      }
      if (item.usedIn) {
        item.usedIn.forEach(recipe => {
          if (!productMap[key].usedIn.includes(recipe)) {
            productMap[key].usedIn.push(recipe);
          }
        });
      }
      if (item.recipeContributions) {
        productMap[key].recipeContributions = [
          ...(productMap[key].recipeContributions || []),
          ...item.recipeContributions
        ];
      }
      if (!productMap[key].mergedFrom.includes(item.name)) {
        productMap[key].mergedFrom.push(item.name);
      }
      
      console.log(`[PostMatchConsolidate] Merged "${item.name}" → ${productMap[key].totalQty}${existingUnit}`);
    }
  });
  
  const consolidated = Object.values(productMap);
  
  // Log merges
  const mergedProducts = consolidated.filter(p => p.mergedFrom && p.mergedFrom.length > 1);
  if (mergedProducts.length > 0) {
    console.log('[PostMatchConsolidate] 📦 Merged products:', mergedProducts.map(p => ({
      productName: p.productName,
      mergedFrom: p.mergedFrom,
      totalQty: p.totalQty,
      unit: p.unit
    })));
  }
  
  // ===== ADDITIONAL CONSOLIDATION BY PRODUCT NAME =====
  // Sometimes the same ingredient matches to DIFFERENT productIds in Typesense
  // (e.g., "smoked paprika" → two different paprika products)
  // This step merges items with the same PRODUCT NAME but different productIds
  
  /**
   * Helper: Get the best product between two items
   * Prefers the product with more sizes and lower min price
   */
  const getBetterProduct = (itemA, itemB) => {
    const productA = itemA.product;
    const productB = itemB.product;
    
    // If one has no product, prefer the one with product
    if (!productA && productB) return itemB;
    if (productA && !productB) return itemA;
    if (!productA && !productB) return itemA;
    
    // Prefer product with more sizes (more options)
    const sizesA = productA.sizes?.length || 0;
    const sizesB = productB.sizes?.length || 0;
    if (sizesA > sizesB) return itemA;
    if (sizesB > sizesA) return itemB;
    
    // If same number of sizes, prefer lower min price
    const minPriceA = Math.min(...(productA.sizes?.map(s => s.price) || [Infinity]));
    const minPriceB = Math.min(...(productB.sizes?.map(s => s.price) || [Infinity]));
    return minPriceA <= minPriceB ? itemA : itemB;
  };
  
  const byProductName = {};
  consolidated.forEach(item => {
    // Normalize product name for comparison (lowercase, trim)
    const normalizedProductName = (item.productName || item.name || '').toLowerCase().trim();
    const measurementType = item.measurementType || 'unknown';
    const nameKey = `${normalizedProductName}_${measurementType}`;
    
    if (!byProductName[nameKey]) {
      byProductName[nameKey] = { ...item };
    } else {
      // Merge: same product name, same measurement type
      const existing = byProductName[nameKey];
      
      // Check unit compatibility
      const unitsCompatible = existing.unit === item.unit || 
        (existing.unit === 'g' && item.unit === 'g') ||
        (existing.unit === 'ml' && item.unit === 'ml');
      
      if (unitsCompatible) {
        console.log(`[PostMatchConsolidate] 🔗 Merging by productName: "${normalizedProductName}" (${existing.totalQty} + ${item.totalQty} ${item.unit})`);
        
        // Choose the better product (more sizes, lower price)
        const betterItem = getBetterProduct(existing, item);
        const betterProduct = betterItem.product;
        const betterProductId = betterItem.productId;
        
        // Update with combined qty and better product
        existing.totalQty += item.totalQty || 0;
        if (betterProduct && betterProduct !== existing.product) {
          existing.product = betterProduct;
          existing.productId = betterProductId;
          existing.lang = betterItem.lang || null;
          existing.productName = betterItem.productName || existing.productName;
          console.log(`[PostMatchConsolidate] 📦 Using better product: ${betterProductId}`);
        }
        
        // Merge metadata
        if (item.allOriginals) {
          item.allOriginals.forEach(orig => {
            if (!existing.allOriginals?.includes(orig)) {
              existing.allOriginals = existing.allOriginals || [];
              existing.allOriginals.push(orig);
            }
          });
        }
        if (item.usedIn) {
          item.usedIn.forEach(recipe => {
            if (!existing.usedIn?.includes(recipe)) {
              existing.usedIn = existing.usedIn || [];
              existing.usedIn.push(recipe);
            }
          });
        }
        if (item.mergedFrom) {
          item.mergedFrom.forEach(src => {
            if (!existing.mergedFrom?.includes(src)) {
              existing.mergedFrom = existing.mergedFrom || [];
              existing.mergedFrom.push(src);
            }
          });
        }
      } else {
        // Different units within same productName - keep separate
        const altKey = `${nameKey}_${item.unit}`;
        byProductName[altKey] = { ...item };
      }
    }
  });
  
  const finalConsolidated = Object.values(byProductName);
  
  // Log final merges by product name
  if (finalConsolidated.length < consolidated.length) {
    console.log(`[PostMatchConsolidate] ✅ Additional merge by productName: ${consolidated.length} → ${finalConsolidated.length} items`);
  }
  
  return finalConsolidated;
};

/**
 * Match ingredients to products in the database (metric)
 * @param {Array} ingredients - Consolidated ingredients list
 * @param {Array} products - Country-specific product database
 * @param {string} country - Country code for analytics tracking
 * @returns {Array} Matched products
 */
export const matchIngredientsToProductsMetric = async (
  ingredients,
  products,
  country = "spain"
) => {
  const dbMatch = await searchIngredientsInTypesense(ingredients, country);
  console.log(" ^ ^ ^ ^ ^ ^ ^ ^ ^ ^ ^ ^ ^ ^ ", dbMatch);

  return dbMatch;
};

/**
 * Build a consumed-only per-recipe cost breakdown for a product.
 * consumedCost = actualPrice × consumedFraction (the value of what's actually
 * eaten, excluding leftover waste). It is then distributed across the recipes
 * that use the product, weighted by each recipe's contributed quantity (with an
 * equal split fallback when contribution qtys are missing/zero), and aggregated
 * by recipe title.
 * @param {Array} recipeContributions - [{ title, ingredientIndex, qty }]
 * @param {number} consumedCost - actualPrice × consumedFraction
 * @returns {Array} [{ title, cost }]
 */
const buildRecipeCosts = (recipeContributions, consumedCost) => {
  const contribs = Array.isArray(recipeContributions) ? recipeContributions : [];
  if (contribs.length === 0 || !(consumedCost > 0)) return [];

  const qtySum = contribs.reduce(
    (s, c) => s + (Number(c.qty) > 0 ? Number(c.qty) : 0),
    0
  );
  const useEqual = !(qtySum > 0);

  const byTitle = {};
  contribs.forEach((c) => {
    const share = useEqual
      ? 1 / contribs.length
      : (Number(c.qty) > 0 ? Number(c.qty) : 0) / qtySum;
    byTitle[c.title] = (byTitle[c.title] || 0) + consumedCost * share;
  });

  return Object.entries(byTitle).map(([title, cost]) => ({ title, cost }));
};

/**
 * Calculate purchase quantities for matched products (metric) using three-parameter evaluation
 */
export const calculatePurchaseQuantitiesMetric = (
  matchedProducts,
  countryProducts,
  currencySymbol,
  userSegment = "standard"
) => {
  console.log('[TESTFLOW] calculatePurchaseQuantitiesMetric input:', matchedProducts.map(p => ({
    name: p.name,
    unmatched: p.unmatched,
    hasProduct: !!p.product,
    hasSizes: !!p.product?.sizes,
    sizesCount: p.product?.sizes?.length
  })));

  return matchedProducts.map((item) => {
    if (item.unmatched) {
      console.log('[TESTFLOW] Skipping unmatched item:', item.name);
      return item;
    }

    const product = item.product;
    if (!product || !product.sizes) {
      console.log('[TESTFLOW] Missing product/sizes for:', item.name, { hasProduct: !!product, hasSizes: !!product?.sizes });
      // FIX: Return item with estimated price based on category average
      const estimatedPrice = CATEGORY_PRICING_DEFAULTS[normalizeCategory(item.category)] || 3.00;
      return {
        ...item,
        totalPrice: `${currencySymbol}${estimatedPrice.toFixed(2)}`,
        consumedCost: estimatedPrice,
        recipeCosts: buildRecipeCosts(item.recipeContributions, estimatedPrice),
        error: "No product data available - estimated price"
      };
    }

    let needInBase = item.totalQty;

    const bestOption = evaluateProductOptions(
      product.sizes,
      needInBase,
      userSegment,
      item.measurementType
    );

    if (!bestOption) {
      console.warn(`No suitable product size found for ${item.name}`);
      console.warn(`  → sizes available:`, product.sizes?.map(s => ({type: s.type, weight: s.weight, weightUnit: s.weightUnit, itemsQty: s.itemsQty, price: s.price})));
      console.warn(`  → needInBase: ${needInBase}, measurementType: ${item.measurementType}`);
      
      // FIX: Try to use first available size as fallback instead of just estimating
      if (product.sizes && product.sizes.length > 0) {
        const fallbackSize = product.sizes[0];
        console.log(`  → Using fallback size:`, fallbackSize);
        
        // Calculate price based on fallback size
        let fallbackPrice = fallbackSize.price || CATEGORY_PRICING_DEFAULTS[normalizeCategory(item.category)] || 3.00;
        
        // For weight products, calculate proportional price
        if (fallbackSize.type === 'weight' || fallbackSize.type === 'package') {
          let sizeWeight = fallbackSize.weight || 1000; // Default 1kg
          if (fallbackSize.weightUnit === 'kg') sizeWeight *= 1000;
          
          const packagesNeeded = smartPackagesNeeded(needInBase, sizeWeight);
          fallbackPrice = fallbackSize.price * packagesNeeded;
          
          return {
            ...item,
            totalPrice: `${currencySymbol}${fallbackPrice.toFixed(2)}`,
            selectedSize: fallbackSize,
            quantityToPurchase: needInBase,
            itemsQuantity: packagesNeeded,
            consumedCost: fallbackPrice,
            recipeCosts: buildRecipeCosts(item.recipeContributions, fallbackPrice),
            fallbackUsed: true
          };
        }

        return {
          ...item,
          totalPrice: `${currencySymbol}${fallbackPrice.toFixed(2)}`,
          selectedSize: fallbackSize,
          consumedCost: fallbackPrice,
          recipeCosts: buildRecipeCosts(item.recipeContributions, fallbackPrice),
          fallbackUsed: true
        };
      }
      
      // Ultimate fallback: estimate price
      const estimatedPrice = CATEGORY_PRICING_DEFAULTS[normalizeCategory(item.category)] || 3.00;
      return {
        ...item,
        totalPrice: `${currencySymbol}${estimatedPrice.toFixed(2)}`,
        consumedCost: estimatedPrice,
        recipeCosts: buildRecipeCosts(item.recipeContributions, estimatedPrice),
        error: "No suitable product size found - estimated price"
      };
    }

    // ===== EARLY SANITY CHECK: Detect corrupted price data =====
    // If price is absurdly high but referencePrice is reasonable, use referencePrice
    // This protects against database export bugs (e.g., price: 1282.05 instead of 12.95)
    const PRICE_SANITY_THRESHOLD = 100; // €100 is max reasonable price per package/unit
    const REFERENCE_PRICE_MAX = 50;     // Reference price should be < €50/kg or /unit
    
    if (bestOption.price > PRICE_SANITY_THRESHOLD && bestOption.referencePrice && bestOption.referencePrice < REFERENCE_PRICE_MAX) {
      console.warn(`🚨 PRICE DATA SANITY CHECK: "${item.name}" has suspicious price €${bestOption.price}`);
      console.warn(`   → referencePrice: €${bestOption.referencePrice}/${bestOption.referenceFormat || 'unit'}`);
      console.warn(`   → Using referencePrice for calculation instead`);
      
      // Override price with calculated value from referencePrice
      // referencePrice is typically per kg or per unit
      if (bestOption.referenceFormat === 'kg' || bestOption.referenceFormat === 'kilo') {
        // referencePrice is per kg, so package price = referencePrice × weight(kg)
        const weightInKg = (bestOption.weight || 1000) / 1000;
        bestOption.price = bestOption.referencePrice * weightInKg;
        console.log(`   → Corrected price: €${bestOption.price.toFixed(2)} (${weightInKg}kg × €${bestOption.referencePrice}/kg)`);
      } else {
        // referencePrice is per unit/piece
        bestOption.price = bestOption.referencePrice;
        console.log(`   → Corrected price: €${bestOption.price.toFixed(2)} (per unit)`);
      }
    }

    // ===== SANITY CHECK: Package without weight =====
    // For package products without weight, when measurementType is 'weight',
    // we need to estimate weight or fall back to buying 1 package
    if (bestOption.type === 'package' && !bestOption.weight && item.measurementType === 'weight') {
      console.warn(`⚠️ Package product "${item.name}" has no weight, estimating for ${needInBase}g requirement`);
      
      // Try to get avgWeight from metadata or use category default
      const avgWeight = getAvgWeightWithFallback(bestOption, item.name, item.category);
      
      if (avgWeight && avgWeight > 0) {
        // Use avgWeight to estimate package weight
        const estimatedPackageWeight = bestOption.itemsQty ? avgWeight * bestOption.itemsQty : avgWeight;
        bestOption.weight = estimatedPackageWeight;
        bestOption.weightUnit = 'g';
        console.log(`   → Estimated package weight: ${estimatedPackageWeight}g (avgWeight: ${avgWeight}g × ${bestOption.itemsQty || 1} items)`);
      } else {
        // Category-based fallback weights
        const categoryWeights = {
          'produce': 150,      // 150g for produce bunches
          'vegetables': 150,
          'herbs': 30,         // 30g for herb bunches
          'meat': 500,         // 500g for meat packages
          'seafood': 400,      // 400g for seafood
          'dairy': 250,        // 250g for dairy
          'default': 200       // 200g default
        };
        
        const category = normalizeCategory(item.category || '').toLowerCase();
        const fallbackWeight = categoryWeights[category] || categoryWeights['default'];
        bestOption.weight = fallbackWeight;
        bestOption.weightUnit = 'g';
        console.log(`   → Using category fallback weight: ${fallbackWeight}g (category: ${category})`);
      }
    }

    // For unit products with weight requirements, convert weight → count using
    // Typesense-first avgWeight (getAvgWeightWithFallback).  This now runs for ALL
    // items that arrive as 'weight' matched to a unit-type Typesense product — because
    // the coercion phase (Phase 3) intentionally skips weight→count so that this block
    // can use the Typesense store-unit weight (e.g. 247g/tray) rather than the
    // PRODUCT_METADATA kitchen-piece weight (e.g. 150g/fillet).
    // originalWeightNeed is stored so calculatePurchaseDetails computes the correct
    // leftover: unitsNeeded × avgWeight − originalWeightNeed (not 0).
    // Package-type products are left alone here — their calculatePurchaseDetails case
    // already handles weight input correctly via size.weight.
    let originalWeightNeed = null;
    if (
      bestOption.type === "unit" &&
      item.measurementType === "weight"
    ) {
      // Get avgWeight with fallback to PRODUCT_METADATA and category defaults
      const avgWeight = getAvgWeightWithFallback(bestOption, item.name, item.category);
      
      if (avgWeight && avgWeight > 0) {
        // Store original weight requirement for accurate leftover calculation
        originalWeightNeed = needInBase;
        
        // Convert required weight to count
        const unitsNeeded = Math.ceil(needInBase / avgWeight);
        console.log(
          `🔧 Converting ${needInBase}g to ${unitsNeeded} units for ${item.name} (avgWeight: ${avgWeight}g)`
        );
        needInBase = unitsNeeded; // This should be the count, not weight

        // Ensure measurement type is updated for proper calculation
        item.measurementType = "count";
      }
      // If no avgWeight available even with fallback, measurementType stays "weight"
      // and calculatePurchaseDetails will handle it
    }

    const purchaseDetails = calculatePurchaseDetails(
      bestOption,
      needInBase,
      item.measurementType,
      item.name,
      originalWeightNeed,
      item.category
    );

    const _gcNeededUnit = originalWeightNeed != null ? 'g'
      : item.measurementType === 'volume' ? 'ml'
      : item.measurementType === 'weight' ? 'g' : 'pcs';
    const _gcLeftoverUnit = bestOption.type === 'unit' ? 'pcs'
      : (bestOption.weightUnit === 'ml' || bestOption.weightUnit === 'l') ? 'ml'
      : 'g';
    console.log(
      `[GROCERYCHECK] ${item.name} | matched: ${item.productName || '—'} | productId: ${item.productId || '—'} | needed: ${parseFloat((originalWeightNeed ?? needInBase).toFixed(2))}${_gcNeededUnit} | storeUnit: ${bestOption.type} | pkgWeight: ${bestOption.weight != null ? `${bestOption.weight}${bestOption.weightUnit || 'g'}` : 'null'} | avgWeight: ${bestOption.avgWeight != null ? `${bestOption.avgWeight}${bestOption.weightUnit || 'g'}` : 'null'} | itemsQty: ${bestOption.itemsQty ?? 'null'} | buy: ${purchaseDetails.itemsQuantity} | leftovers: ${parseFloat(purchaseDetails.leftovers.toFixed(2))}${_gcLeftoverUnit}`
    );

    // Enhanced price calculation
    let actualPrice = 0;

    switch (bestOption.type) {
      case "unit":
        // Unit products: charge per unit needed
        actualPrice = bestOption.price * purchaseDetails.itemsQuantity;
        break;

      case "weight":
        // Weight products: charge based on actual weight purchased
        console.log(`💰 Price calculation for weight product "${item.name}":`, {
          'bestOption.weight': bestOption.weight,
          'bestOption.weightUnit': bestOption.weightUnit,
          'bestOption.price': bestOption.price,
          'purchaseDetails.quantityToPurchase': purchaseDetails.quantityToPurchase,
          'purchaseDetails.itemsQuantity': purchaseDetails.itemsQuantity,
          'needInBase': needInBase,
          'originalWeightNeed': originalWeightNeed,
          'smartSizing': purchaseDetails.smartSizing
        });
        
        if (bestOption.weight && bestOption.weight > 0) {
          // FIX: Handle unit conversion for weight pricing
          let priceBaseWeight = bestOption.weight;
          if (bestOption.weightUnit === "kg") {
            priceBaseWeight = bestOption.weight * 1000; // Convert to grams
          } else if (bestOption.weightUnit === "l" || bestOption.weightUnit === "L") {
            priceBaseWeight = bestOption.weight * 1000; // Convert liters to ml
          }
          
          // SANITY CHECK: If priceBaseWeight is suspiciously small (< 50g), 
          // it's likely a parsing error where "0.8kg" was parsed as "0.8g"
          // For weight products, minimum base weight should be at least 100g
          if (priceBaseWeight < 50) {
            console.warn(`⚠️ Suspiciously small priceBaseWeight (${priceBaseWeight}g) for weight product "${item.name}"`);
            // Check if the weight looks like it could be in kg (small decimal number)
            if (bestOption.weight < 10 && bestOption.weight > 0) {
              // Likely in kg, convert to grams
              priceBaseWeight = bestOption.weight * 1000;
              console.log(`   → Converted ${bestOption.weight} to ${priceBaseWeight}g (assumed kg)`);
            } else {
              // Fallback: assume price is per kg (1000g)
              priceBaseWeight = 1000;
              console.log(`   → Using fallback priceBaseWeight: 1000g (assumed per kg pricing)`);
            }
          }

          const pricePerGram = bestOption.price / priceBaseWeight;
          actualPrice = pricePerGram * purchaseDetails.quantityToPurchase;
          console.log(`   → priceBaseWeight: ${priceBaseWeight}g, pricePerGram: €${pricePerGram.toFixed(4)}, actualPrice: €${actualPrice.toFixed(2)}`);
        } else {
          // FIX: For weight products without defined weight (sold "granel"/by weight),
          // assume the price is per kg (1000g) - this is standard for bulk products
          console.log(`⚠️ Weight product "${item.name}" has no weight defined, assuming price is per kg`);
          const pricePerGram = bestOption.price / 1000; // Assume price per 1kg
          actualPrice = pricePerGram * purchaseDetails.quantityToPurchase;
          console.log(`   → pricePerGram: €${pricePerGram.toFixed(4)}, actualPrice: €${actualPrice.toFixed(2)}`);
        }
        break;

      case "package":
        // Package products: charge per package needed
        actualPrice = bestOption.price * purchaseDetails.itemsQuantity;
        break;

      default:
        actualPrice = bestOption.price;
    }

    // SANITY CHECK: Detect and fix absurd prices
    // If calculated price is >€100 for a single grocery item, something is wrong
    const MAX_REASONABLE_PRICE = 100;
    if (actualPrice > MAX_REASONABLE_PRICE) {
      console.error(`🚨 PRICE SANITY CHECK FAILED for "${item.name}": €${actualPrice.toFixed(2)}`);
      console.error(`   → bestOption:`, {
        type: bestOption.type,
        price: bestOption.price,
        weight: bestOption.weight,
        weightUnit: bestOption.weightUnit,
        avgWeight: bestOption.avgWeight
      });
      console.error(`   → purchaseDetails:`, purchaseDetails);
      console.error(`   → needInBase: ${needInBase}, measurementType: ${item.measurementType}`);
      console.error(`   → originalWeightNeed: ${originalWeightNeed}`);
      
      // Fallback: recalculate with simple logic
      // For weight products, assume price is per kg and calculate proportionally
      // FIX: Use originalWeightNeed if available (when needInBase was converted from weight to count)
      const weightForCalculation = originalWeightNeed || (
        item.measurementType === 'weight' ? needInBase : 
        item.measurementType === 'count' && bestOption.avgWeight ? needInBase * parseFloat(bestOption.avgWeight) :
        needInBase * 100 // Fallback: assume 100g per unit
      );
      
      if (bestOption.type === 'weight') {
        // For weight products, assume price is per kg
        const pricePerKg = bestOption.price;
        actualPrice = (pricePerKg / 1000) * weightForCalculation;
        console.log(`   → Recalculated as ${weightForCalculation}g at €${pricePerKg}/kg = €${actualPrice.toFixed(2)}`);
      } else if (bestOption.type === 'unit') {
        // For unit products, recalculate based on actual weight needed
        const avgWeight = getAvgWeightWithFallback(bestOption, item.name, item.category);
        if (avgWeight && avgWeight > 0 && originalWeightNeed) {
          const unitsNeeded = Math.ceil(originalWeightNeed / avgWeight);
          actualPrice = bestOption.price * unitsNeeded;
          console.log(`   → Recalculated as ${unitsNeeded} units at €${bestOption.price}/unit = €${actualPrice.toFixed(2)}`);
        } else {
          actualPrice = bestOption.price * purchaseDetails.itemsQuantity;
          console.log(`   → Using unit calculation: €${actualPrice.toFixed(2)}`);
        }
      } else {
        // For package products, use package price
        actualPrice = bestOption.price * purchaseDetails.itemsQuantity;
        console.log(`   → Using package price: €${actualPrice.toFixed(2)}`);
      }
      
      // Final sanity check - if still over limit, cap at reasonable amount
      if (actualPrice > MAX_REASONABLE_PRICE * 2) {
        console.warn(`   → Price still too high (€${actualPrice.toFixed(2)}), capping at reasonable estimate`);
        // Use a reasonable estimate: ~€20/kg for most products as fallback
        actualPrice = (weightForCalculation / 1000) * 20;
        console.log(`   → Final fallback: €${actualPrice.toFixed(2)} (based on €20/kg)`);
      }
    }

    // Apply price adjustment from smart sizing if available
    // REMOVED: This was causing unrealistic price reductions
    // if (purchaseDetails.priceAdjustment) {
    //   actualPrice *= purchaseDetails.priceAdjustment;
    // }

    // Consumed-only cost (excludes leftover waste) attributed back to recipes
    const consumedFraction = typeof purchaseDetails.consumedFraction === 'number'
      ? purchaseDetails.consumedFraction
      : 1;
    const consumedCost = actualPrice * consumedFraction;

    return {
      ...item,
      ...purchaseDetails,
      totalPrice: `${currencySymbol}${actualPrice.toFixed(2)}`,
      selectedSize: bestOption,
      consumedCost,
      recipeCosts: buildRecipeCosts(item.recipeContributions, consumedCost),
      // When smartPackagesNeeded rounds down, align displayed qty with what's actually purchased
      ...(purchaseDetails.adjustedTotalQty !== undefined ? {
        totalQty: purchaseDetails.adjustedTotalQty,
        originalTotalQty: item.totalQty, // kept for recipe scaling ratio
      } : {}),
    };
  });
};

/**
 * Three-parameter evaluation system for product selection
 * @param {Array} sizes - Available product sizes
 * @param {number} needInBase - Required quantity in base units
 * @param {string} userSegment - User's preferred segment
 * @param {string} measurementType - Type of measurement (weight, volume, count)
 * @returns {Object|null} Best product option
 */
const evaluateProductOptions = (
  sizes,
  needInBase,
  userSegment,
  measurementType
) => {
  if (!sizes || sizes.length === 0) return null;

  // Define segment hierarchy (lowest to highest)
  const segmentHierarchy = [
    "survival",
    "budget",
    "standard",
    "gourmet",
    "luxury",
  ];
  const userSegmentIndex = segmentHierarchy.indexOf(userSegment);

  // Step 1: Filter suitable options (can satisfy requirement)
  const suitableOptions = sizes.filter((size) => {
    return isSizeSuitable(size, needInBase, measurementType);
  });

  if (suitableOptions.length === 0) {
    console.warn(`No suitable product options found for needInBase=${needInBase}, measurementType=${measurementType}`);
    return null;
  }

  // Step 2: Apply segment priority - try user segment first, then scope down
  for (let i = userSegmentIndex; i >= 0; i--) {
    const targetSegment = segmentHierarchy[i];
    const segmentOptions = suitableOptions.filter(
      (size) => (size.segment || "standard") === targetSegment
    );

    if (segmentOptions.length > 0) {
      // Step 3: Price optimization within segment
      const cheapestPrice = Math.min(
        ...segmentOptions.map((size) => size.price)
      );
      const cheapestOptions = segmentOptions.filter(
        (size) => size.price === cheapestPrice
      );

      // Step 4: Rating tie-breaking (if multiple options within 10% price difference)
      if (cheapestOptions.length === 1) {
        return cheapestOptions[0];
      }

      // Check for price differences within 10%
      const priceRangeOptions = segmentOptions.filter((size) => {
        const priceDiff = (size.price - cheapestPrice) / cheapestPrice;
        return priceDiff <= 0.1;
      });

      // Return highest rated option within price range
      return priceRangeOptions.reduce((best, current) => {
        const bestRating = best.rating || 3.0;
        const currentRating = current.rating || 3.0;
        return currentRating > bestRating ? current : best;
      });
    }
  }

  // Fallback: cheapest option if no segment match
  return suitableOptions.reduce((cheapest, current) =>
    current.price < cheapest.price ? current : cheapest
  );
};

/**
 * Check if a product size can satisfy the requirement
 * @param {Object} size - Product size option
 * @param {number} needInBase - Required quantity in base units
 * @param {string} measurementType - Type of measurement
 * @returns {boolean} Whether size is suitable
 */
const isSizeSuitable = (size, needInBase, measurementType) => {
  // Debug logging for troubleshooting
  console.log(`[isSizeSuitable] Checking: type=${size.type}, weight=${size.weight}, weightUnit=${size.weightUnit}, needInBase=${needInBase}, measurementType=${measurementType}`);
  
  // FIX: Handle missing or undefined type - default to 'package' for most products
  const sizeType = size.type || 'package';
  
  switch (sizeType) {
    case "unit":
      const availableQty = size.itemsQty || 1;
      
      // Parse avgWeight to handle both string and number types
      const avgWeightNum = parseFloat(size.avgWeight);
      const hasAvgWeight = !isNaN(avgWeightNum) && avgWeightNum > 0;

      // Check if avgWeight represents count (when weightUnit is "ud", "unit", or similar)
      const isCountBasedWeight = size.weightUnit && 
        (size.weightUnit.toLowerCase() === "ud" || 
         size.weightUnit.toLowerCase() === "unit" ||
         size.weightUnit.toLowerCase() === "units");

      if (measurementType === "weight" && hasAvgWeight && !isCountBasedWeight) {
        // For unit products with weight requirements, avgWeight must be reasonable (>= 10g)
        // If avgWeight < 10g, it's likely invalid data or a package weight, not per-unit weight
        if (avgWeightNum < 10) {
          console.warn(`Unit product has unreasonably small avgWeight (${avgWeightNum}g) for weight measurement - rejecting`);
          return false;
        }
        
        const unitsNeeded = Math.ceil(needInBase / avgWeightNum);

        // Allow purchasing reasonable quantities:
        // - If itemsQty > 1: product is sold in packages (e.g., eggs), allow up to 2x packages
        // - If itemsQty === 1: single units (e.g., potatoes), allow up to 20 units for practical shopping
        const maxAllowedUnits = availableQty > 1 ? availableQty * 2 : 20;
        
        if (unitsNeeded <= maxAllowedUnits) {
          return true;
        }

        console.warn(`Unit product requires ${unitsNeeded} units, exceeds max ${maxAllowedUnits} - rejecting`);
        return false;
      } else if (
        measurementType === "volume" &&
        size.weight &&
        size.weightUnit
      ) {
        // For volume-based unit products (like milk cartons, juice boxes)
        let unitVolume = size.weight; // Volume in ml

        // FIX: Convert to ml if needed
        if (size.weightUnit === "l" || size.weightUnit === "L") {
          unitVolume = size.weight * 1000;
        }

        // Check if one unit can satisfy the requirement
        if (needInBase <= unitVolume) {
          return true;
        }

        // Allow buying multiple units if needed (smart volume limits)
        const unitsNeeded = Math.ceil(needInBase / unitVolume);

        // Tier 1: Perfect fit
        if (unitsNeeded <= availableQty) {
          return true;
        }

        // Tier 2: Reasonable flexibility (up to 2x)
        if (unitsNeeded <= availableQty * 2) {
          return true;
        }

        // Tier 3: Practical limit for volume products (up to 4x)
        if (unitsNeeded <= availableQty * 4) {
          return true;
        }

        return false;
      } else {
        // Standard count-based: need 3 eggs, package has 6
        // If avgWeight represents count (weightUnit is "ud", "unit", etc.), use it as items per package
        if (hasAvgWeight && isCountBasedWeight) {
          const itemsPerPackage = avgWeightNum;
          const totalAvailable = itemsPerPackage * availableQty;
          // Check if total available items can satisfy the requirement
          return needInBase <= totalAvailable;
        }
        
        // If avgWeight exists and is reasonable (>= 10g), treat as weight-based for unit products
        // For very small avgWeight (< 10g), it's likely a small package (spices, herbs, etc.), not per-unit weight
        if (hasAvgWeight && avgWeightNum >= 10) {
          const unitsNeeded = Math.ceil(needInBase / avgWeightNum);
          return unitsNeeded <= availableQty * 2;
        }

        // For small packages or simple count-based products, just check against available quantity
        return needInBase <= availableQty * 2;
      }

    case "weight":
      // For weight products, we can buy exact amount (minimum check could be added)
      // Special handling for piece-based requirements on weight products
      const weightAvgWeightNum = parseFloat(size.avgWeight);
      const hasWeightAvgWeight = !isNaN(weightAvgWeightNum) && weightAvgWeightNum > 0;
      if (measurementType === "count" && hasWeightAvgWeight) {
        // Ensure this weight product can supply the required pieces based on avgWeight
        return hasWeightAvgWeight; // As long as avgWeight exists, we can calculate
      }
      return true;

    case "package":
      // FIX: For packages, be more lenient - allow buying multiple packages
      if (size.weight && size.weight > 0) {
        // Convert package weight to base units (grams or ml)
        let packageWeightInBase = size.weight;

        // Handle unit conversion for package weights
        if (size.weightUnit === "kg") {
          packageWeightInBase = size.weight * 1000;
        } else if (size.weightUnit === "l" || size.weightUnit === "L") {
          // For liquid packages, convert liters to ml
          packageWeightInBase = size.weight * 1000;
        }

        // For volume measurements, check if we can satisfy with reasonable number of packages
        if (measurementType === "volume") {
          const packageVolumeInMl =
            size.weightUnit === "l" || size.weightUnit === "L"
              ? size.weight * 1000
              : size.weight;
          // Allow up to 4 packages for volume products
          return packageVolumeInMl * 4 >= needInBase;
        }

        // For weight measurements, allow multiple packages
        if (measurementType === "weight") {
          // Allow up to 4 packages for weight products
          return packageWeightInBase * 4 >= needInBase;
        }

        // For count measurements with packages, always allow (we'll calculate quantity later)
        if (measurementType === "count") {
          if (size.itemsQty && size.itemsQty > 0) {
            // Allow up to 4 packages
            return size.itemsQty * 4 >= needInBase;
          }
          // If no itemsQty, assume package can satisfy count requirement
          return true;
        }

        // Fallback: allow package
        return true;
      } else if (size.itemsQty && size.itemsQty > 0) {
        // Count-based package: allow multiple packages
        return size.itemsQty * 4 >= needInBase;
      } else {
        // Fallback: allow package if no clear quantity info (we'll estimate later)
        return true;
      }

    default:
      return false;
  }
};

/**
 * Calculate purchase details based on product type and requirements
 * @param {Object} size - Selected product size
 * @param {number} needInBase - Required quantity in base units
 * @param {string} measurementType - Type of measurement
 * @param {string} productName - Product name for metadata lookup
 * @param {number} originalWeightNeed - Original weight requirement (for unit products converted from weight)
 * @param {string} category - Product category for default avgWeight fallback

 * @returns {Object} Purchase details
 */
/**
 * Smart ceiling for package count: rounds DOWN if the fractional overage is ≤ threshold.
 * Example: need=390g, packageSize=380g → ratio=1.026 → 1 package (2.6% over, within 20% → round down)
 * Example: need=460g, packageSize=380g → ratio=1.21 → 2 packages (21% over → round up)
 * @param {number} need - Required quantity in base units
 * @param {number} packageSize - Size of one package in base units
 * @param {number} threshold - Fractional overage threshold (default 0.20 = 20%)
 * @returns {number} Number of packages to buy
 */
const smartPackagesNeeded = (need, packageSize, threshold = 0.20) => {
  if (packageSize <= 0) return 1;
  const ratio = need / packageSize;
  const floor = Math.floor(ratio);
  if (floor === 0) return 1; // always need at least 1 package
  const fractional = ratio - floor;
  if (fractional > 0 && fractional <= threshold) {
    return floor; // within threshold — round down to avoid buying an almost-empty extra package
  }
  return Math.ceil(ratio);
};

const calculatePurchaseDetails = (
  size,
  needInBase,
  measurementType,
  productName = null,
  originalWeightNeed = null,
  category = null
) => {
  const details = {
    quantityToPurchase: 0,
    itemsQuantity: 1,
    unitSize: "",
    leftovers: 0,
    leftoverPercentage: "0%",
    // Fraction of the purchased amount actually consumed by recipes (0..1).
    // Defaults to 1 (no leftovers); branches that produce leftovers override it.
    // Used downstream to compute consumed-only per-recipe cost.
    consumedFraction: 1,
  };

  switch (size.type) {
    case "unit":
      // FIX: Handle volume-based unit products (like milk cartons)
      if (measurementType === "volume" && size.weight && size.weightUnit) {
        let unitVolume = size.weight;

        // Convert to ml if needed
        if (size.weightUnit === "l" || size.weightUnit === "L") {
          unitVolume = size.weight * 1000;
        }

        const volumeNeeded = needInBase; // needInBase is in ml
        const unitsNeeded = smartPackagesNeeded(volumeNeeded, unitVolume);
        details.itemsQuantity = unitsNeeded;
        details.quantityToPurchase = unitsNeeded * unitVolume;
        details.unitSize = `${unitsNeeded} × ${
          size.description || "unit"
        } (${unitVolume}ml each)`;

        const totalVolume = unitsNeeded * unitVolume;
        details.leftovers = Math.max(0, totalVolume - volumeNeeded);
        details.leftoverPercentage =
          totalVolume > 0
            ? `${Math.round((details.leftovers / totalVolume) * 100)}%`
            : "0%";
        details.consumedFraction =
          totalVolume > 0
            ? Math.min(1, Math.max(0, (totalVolume - details.leftovers) / totalVolume))
            : 1;
        // If rounded down, align displayed qty to what's actually being purchased
        if (totalVolume < volumeNeeded) {
          details.adjustedTotalQty = totalVolume;
          details.quantityRatio = totalVolume / volumeNeeded;
        }
      }
      // Handle weight-based requirements for unit products with avgWeight
      else if (
        measurementType === "weight"
      ) {
        // Get avgWeight with fallback to PRODUCT_METADATA and category defaults
        const avgWeight = getAvgWeightWithFallback(size, productName, category);
        
        if (avgWeight && avgWeight > 0) {
          const unitsNeeded = smartPackagesNeeded(needInBase, avgWeight);
          const totalWeight = unitsNeeded * avgWeight;

          details.itemsQuantity = unitsNeeded;
          details.quantityToPurchase = unitsNeeded; // Keep as unit count for unit products
          details.unitSize = `${unitsNeeded} × ${size.description || "unit"}`;

          // Calculate leftovers based on actual weight
          details.leftovers = Math.max(0, totalWeight - needInBase);
          details.leftoverPercentage =
            totalWeight > 0
              ? `${Math.round((details.leftovers / totalWeight) * 100)}%`
              : "0%";
          details.consumedFraction =
            totalWeight > 0
              ? Math.min(1, Math.max(0, (totalWeight - details.leftovers) / totalWeight))
              : 1;
          // If rounded down, align displayed qty and store ratio for recipe scaling
          if (totalWeight < needInBase) {
            details.adjustedTotalQty = totalWeight;
            details.quantityRatio = totalWeight / needInBase;
          }
        } else {
          // Last resort fallback - shouldn't happen with category defaults, but just in case
          // Assume 1 unit = ~100g as a safe default
          const fallbackAvgWeight = 100;
          const unitsNeeded = Math.ceil(needInBase / fallbackAvgWeight);
          details.itemsQuantity = unitsNeeded;
          details.quantityToPurchase = unitsNeeded;
          details.unitSize = `${unitsNeeded} × ${size.description || "unit"}`;
          console.warn(`⚠️ No avgWeight found for ${productName}, using fallback ${fallbackAvgWeight}g`);
        }
      }
      // Handle count-based requirements (including converted from weight)
      else {
        // Standard unit calculation for count measurement type
        const unitsNeeded = Math.ceil(needInBase);
        details.itemsQuantity = unitsNeeded;
        details.quantityToPurchase = unitsNeeded;
        details.unitSize = `${unitsNeeded} × ${size.description || "unit"}`;
        
        // Calculate leftovers using avgWeight with fallback
        const avgWeight = getAvgWeightWithFallback(size, productName, category);
        
        if (avgWeight && avgWeight > 0) {
          const totalWeight = unitsNeeded * avgWeight;
          // Use originalWeightNeed if available (for unit products converted from weight requirements)
          const weightNeeded = originalWeightNeed || (needInBase * avgWeight);
          details.leftovers = Math.max(0, totalWeight - weightNeeded);
          details.leftoverPercentage = totalWeight > 0
            ? `${Math.round((details.leftovers / totalWeight) * 100)}%`
            : '0%';
          details.consumedFraction = totalWeight > 0
            ? Math.min(1, Math.max(0, (totalWeight - details.leftovers) / totalWeight))
            : 1;
        }
      }
      break;

    case "weight":
      // For weight products being purchased by piece count
      const pkgAvgWeightNum = parseFloat(size.avgWeight);
      const hasPkgAvgWeight = !isNaN(pkgAvgWeightNum) && pkgAvgWeightNum > 0;
      
      if (measurementType === "count" && hasPkgAvgWeight) {
        const weightNeeded = needInBase * pkgAvgWeightNum;
        details.itemsQuantity = needInBase; // Number of pieces
        details.quantityToPurchase = weightNeeded;
        details.unitSize = `${needInBase} × ${size.description || "item"} (~${weightNeeded}g)`;
        details.leftovers = 0; // No leftovers for exact weight purchases
      } else if (
        (hasPkgAvgWeight ||
          (productName && findProductMetadata(productName)?.avgWeight)) &&
        productName
      ) {
        // Use smart weight sizing for weight products with avgWeight or metadata avgWeight
        const metadata = findProductMetadata(productName);
        const smartSizing = calculateSmartWeightSizing(
          size,
          needInBase,
          metadata,
          "metric"
        );
        return smartSizing;
      } else {
        // Standard weight purchase
        details.itemsQuantity = 1; // Always 1 for weight products
        details.quantityToPurchase = needInBase;
        details.unitSize = `${needInBase}${size.weightUnit || "g"}`;

        // Add approximate count if avgWeight available
        if (size.avgWeight && size.avgWeight > 0) {
          const approxCount = Math.round(needInBase / size.avgWeight);
          details.unitSize += ` (approximately ${approxCount} items)`;
        }
        details.leftovers = 0; // No leftovers for exact weight purchases
      }
      break;

    case "package":
      // FIX: Handle count-based packages FIRST (eggs, bread), then weight-based, then volume
      // Count-based packages should use itemsQty for calculation, not weight
      const isCountBasedPackage = size.itemsQty && size.itemsQty > 1 && measurementType === 'count';
      
      if (isCountBasedPackage) {
        // Count-based package (like eggs 12-pack, bread loaf)
        const packagesNeeded = smartPackagesNeeded(needInBase, size.itemsQty);
        details.itemsQuantity = packagesNeeded;
        details.quantityToPurchase = packagesNeeded * size.itemsQty;
        details.unitSize = `${packagesNeeded} × ${
          size.description || "package"
        }`;

        const totalItems = packagesNeeded * size.itemsQty;
        details.leftovers = Math.max(0, totalItems - needInBase);
        details.leftoverPercentage =
          totalItems > 0
            ? `${Math.round((details.leftovers / totalItems) * 100)}%`
            : "0%";
        details.consumedFraction =
          totalItems > 0
            ? Math.min(1, Math.max(0, (totalItems - details.leftovers) / totalItems))
            : 1;
      } else if (size.weight && size.weight > 0) {
        // Convert package weight to base units if needed
        let packageWeightInBase = size.weight;

        if (size.weightUnit === "kg") {
          packageWeightInBase = size.weight * 1000; // Convert kg to g
        } else if (size.weightUnit === "l" || size.weightUnit === "L") {
          packageWeightInBase = size.weight * 1000; // Convert L to ml
        }

        // When the recipe specifies count (e.g. "8 onions", "1 leek") but the
        // Typesense product has no size.itemsQty, we convert pieces → grams
        // using PRODUCT_METADATA per-piece avgWeight before calling
        // smartPackagesNeeded. We deliberately skip size.avgWeight here because
        // for package products it is the total package weight, not per-piece.
        let needForPkg = needInBase;
        if (measurementType === 'count' && productName) {
          const meta = findProductMetadata(productName);
          const perPieceWeight = meta?.avgWeight?.metric
            || DEFAULT_AVG_WEIGHTS_METRIC[normalizeCategory(category || 'Other')]
            || DEFAULT_AVG_WEIGHTS_METRIC['Other'];
          needForPkg = needInBase * perPieceWeight;
        }

        // Weight/volume-based package (like spinach 450g, olive oil 500ml)
        const packagesNeeded = smartPackagesNeeded(needForPkg, packageWeightInBase);
        details.itemsQuantity = packagesNeeded;
        details.quantityToPurchase = packagesNeeded * packageWeightInBase;
        details.unitSize = `${packagesNeeded} × ${
          size.description || "package"
        }`;

        const totalPackageWeight = packagesNeeded * packageWeightInBase;
        details.leftovers = Math.max(0, totalPackageWeight - needForPkg);
        details.leftoverPercentage =
          totalPackageWeight > 0
            ? `${Math.round((details.leftovers / totalPackageWeight) * 100)}%`
            : "0%";
        details.consumedFraction =
          totalPackageWeight > 0
            ? Math.min(1, Math.max(0, (totalPackageWeight - details.leftovers) / totalPackageWeight))
            : 1;
        // If rounded down, align displayed qty and store ratio for recipe scaling
        if (totalPackageWeight < needForPkg) {
          details.adjustedTotalQty = totalPackageWeight;
          details.quantityRatio = totalPackageWeight / needForPkg;
        }
      } else if (size.itemsQty && size.itemsQty > 0) {
        // Count-based package fallback (when measurementType is not 'count')
        const packagesNeeded = smartPackagesNeeded(needInBase, size.itemsQty);
        details.itemsQuantity = packagesNeeded;
        details.quantityToPurchase = packagesNeeded * size.itemsQty;
        details.unitSize = `${packagesNeeded} × ${
          size.description || "package"
        }`;

        const totalItems = packagesNeeded * size.itemsQty;
        details.leftovers = Math.max(0, totalItems - needInBase);
        details.leftoverPercentage =
          totalItems > 0
            ? `${Math.round((details.leftovers / totalItems) * 100)}%`
            : "0%";
        details.consumedFraction =
          totalItems > 0
            ? Math.min(1, Math.max(0, (totalItems - details.leftovers) / totalItems))
            : 1;
      } else {
        // Fallback for packages without clear weight or count info
        details.itemsQuantity = 1;
        details.quantityToPurchase = needInBase;
        details.unitSize = `1 × ${size.description || "package"}`;
        details.leftovers = 0;
      }
      break;

    default:
      // Fallback for unknown product types
      details.itemsQuantity = 1;
      details.quantityToPurchase = needInBase;
      details.unitSize = `${needInBase} ${
        measurementType === "weight" ? "g" : "units"
      }`;
      details.leftovers = 0;
      break;
  }

  return details;
};

/**
 * Calculate smart weight product sizing with avgWeight-based variants
 * @param {Object} size - Weight product size
 * @param {number} needInBase - Required quantity in grams
 * @param {Object} metadata - Product metadata with avgWeight
 * @param {string} system - Measurement system ('metric' or 'imperial')
 * @returns {Object} Optimal sizing details
 */
const calculateSmartWeightSizing = (
  size,
  needInBase,
  metadata,
  system = "metric"
) => {
  if (!metadata?.avgWeight?.[system] && !size.avgWeight) {
    // Fallback to standard weight calculation without smart sizing
    return {
      quantityToPurchase: needInBase,
      itemsQuantity: 1,
      unitSize: `${needInBase}d`,
      leftovers: 0,
      leftoverPercentage: "0%",
      unitInfo: null,
      priceAdjustment: 1.0,
    };
  }

  const avgWeight = size.avgWeight || metadata.avgWeight[system];
  
  // SANITY CHECK: avgWeight should be reasonable (at least 5g per piece)
  // If avgWeight is too small, skip smart sizing and use standard weight calculation
  if (avgWeight < 5) {
    console.warn(`⚠️ avgWeight too small (${avgWeight}g), skipping smart sizing`);
    return {
      quantityToPurchase: needInBase,
      itemsQuantity: 1,
      unitSize: `${needInBase}g`,
      leftovers: 0,
      leftoverPercentage: "0%",
      unitInfo: null,
      priceAdjustment: 1.0,
    };
  }

  // Calculate small/medium/large variants (±25% of avgWeight)
  const variants = {
    small: avgWeight * 0.75,
    medium: avgWeight,
    large: avgWeight * 1.25,
  };

  // Find best combination with minimal leftover
  const combinations = [];
  
  // OPTIMIZATION: Limit max units to prevent excessive combinations
  const maxUnits = Math.min(Math.ceil(needInBase / variants.small) + 2, 50);

  // Try different combinations of small/medium/large units
  for (
    let large = 0;
    large <= Math.min(Math.ceil(needInBase / variants.large) + 1, maxUnits);
    large++
  ) {
    for (
      let medium = 0;
      medium <=
      Math.min(Math.ceil((needInBase - large * variants.large) / variants.medium) + 1, maxUnits - large);
      medium++
    ) {
      const remaining =
        needInBase - large * variants.large - medium * variants.medium;
      const small = remaining > 0 ? Math.ceil(remaining / variants.small) : 0;
      
      // Skip if total units exceed reasonable limit
      if (large + medium + small > maxUnits) continue;

      const totalWeight =
        large * variants.large +
        medium * variants.medium +
        small * variants.small;

      if (totalWeight >= needInBase) {
        const leftover = totalWeight - needInBase;
        const leftoverPercentage = (leftover / totalWeight) * 100;

        combinations.push({
          large,
          medium,
          small,
          totalWeight,
          leftover,
          leftoverPercentage,
          totalUnits: large + medium + small,
        });
      }
    }
  }

  // Find combination with minimal leftover percentage
  const bestCombination = combinations.reduce((best, current) => {
    if (current.leftoverPercentage < best.leftoverPercentage) {
      return current;
    }
    if (
      current.leftoverPercentage === best.leftoverPercentage &&
      current.totalUnits < best.totalUnits
    ) {
      return current;
    }
    return best;
  }, combinations[0]);

  if (!bestCombination) {
    // Fallback if no suitable combination found
    return {
      quantityToPurchase: needInBase,
      itemsQuantity: 1,
      unitSize: `${needInBase}g`,
      leftovers: 0,
      leftoverPercentage: "0%",
      unitInfo: null,
      priceAdjustment: 1.0,
    };
  }

  // Create unit information string
  const unitParts = [];
  const unitBreakdown = [];
  if (bestCombination.large > 0) {
    unitParts.push(
      `${bestCombination.large} large (~${Math.round(variants.large)}g each)`
    );
    unitBreakdown.push({
      size: "large",
      count: bestCombination.large,
      weightPerUnit: Math.round(variants.large),
      weightUnit: "g",
    });
  }
  if (bestCombination.medium > 0) {
    unitParts.push(
      `${bestCombination.medium} medium (~${Math.round(variants.medium)}g each)`
    );
    unitBreakdown.push({
      size: "medium",
      count: bestCombination.medium,
      weightPerUnit: Math.round(variants.medium),
      weightUnit: "g",
    });
  }
  if (bestCombination.small > 0) {
    unitParts.push(
      `${bestCombination.small} small (~${Math.round(variants.small)}g each)`
    );
    unitBreakdown.push({
      size: "small",
      count: bestCombination.small,
      weightPerUnit: Math.round(variants.small),
      weightUnit: "g",
    });
  }

  const unitInfo = unitParts.join(" + ");
  const totalUnits = bestCombination.totalUnits;

  // Calculate price adjustment if leftover exceeds 5%
  let priceAdjustment = 1.0;
  if (bestCombination.leftoverPercentage > 5) {
    // Reduce effective price proportionally to account for leftover
    priceAdjustment = needInBase / bestCombination.totalWeight;
  }

  return {
    quantityToPurchase: Math.round(bestCombination.totalWeight),
    itemsQuantity: totalUnits,
    unitSize: `${Math.round(
      bestCombination.totalWeight
    )}g (~${totalUnits} units)`,
    leftovers: Math.round(bestCombination.leftover),
    leftoverPercentage: `${Math.round(bestCombination.leftoverPercentage)}%`,
    consumedFraction: bestCombination.totalWeight > 0
      ? Math.min(1, Math.max(0, (bestCombination.totalWeight - bestCombination.leftover) / bestCombination.totalWeight))
      : 1,
    unitInfo,
    unitBreakdown,
    priceAdjustment,
    smartSizing: true,
  };
};

/**
 * Calculate leftovers from grocery list (metric)
 * @param {Array} list - Complete grocery list
 * @returns {Array} Leftover items
 */
export const calculateLeftoversMetric = (list) => {
  return list
    .filter((item) => item.leftovers && item.leftovers > 0)
    .map((item) => {
      // Determine the unit based on the product type and how leftovers were calculated
      let leftoverUnit = 'g'; // Default to grams for most products
      const selectedSize = item.selectedSize;
      const productType = selectedSize?.type;
      const measurementType = item.measurementType;
      const productName = (item.productName || item.name || '').toLowerCase();
      
      // Check if this is a SOLID/POWDER/PASTE product that should NEVER be in ml
      // Also includes products that are packaged by weight even if they have liquid-like names
      const isSolidOrPowder = productName.includes('powder') || productName.includes('cocoa') ||
                               productName.includes('flour') || productName.includes('sugar') ||
                               productName.includes('spice') || productName.includes('paprika') ||
                               productName.includes('cinnamon') || productName.includes('cumin') ||
                               productName.includes('nut') || productName.includes('seed') ||
                               productName.includes('honey') || productName.includes('miel') ||  // Honey is sold by weight
                               productName.includes('jam') || productName.includes('mermelada') ||  // Jam is sold by weight
                               productName.includes('tuna') || productName.includes('atun') ||  // Canned tuna is weight
                               productName.includes('coriander') || productName.includes('cilantro') ||  // Spices
                               productName.includes('oregano') || productName.includes('basil') ||
                               productName.includes('parsley') || productName.includes('thyme') ||
                               productName.includes('rosemary') || productName.includes('ginger') ||
                               productName.includes('pepper') || productName.includes('salt') ||
                               productName.includes('curry') || productName.includes('turmeric') ||
                               productName.includes('chili') || productName.includes('garlic');  // Ground/dried versions
      
      // Check if this is a truly count-based product (eggs, bread only)
      const isTrulyCountBased = productName.includes('egg') || productName.includes('huevo') ||
                                 productName.includes('bread') || productName.includes('pan ');
      
      // Check if this is a liquid/volume product
      const isVolumeProduct = productName.includes('milk') || productName.includes('leche') ||
                               productName.includes('cream') || productName.includes('nata') ||
                               productName.includes('juice') || productName.includes('zumo') ||
                               productName.includes('water') || productName.includes('agua') ||
                               productName.includes('oil') || productName.includes('aceite') ||
                               productName.includes('sauce') || productName.includes('salsa') ||
                               productName.includes('wine') || productName.includes('vino') ||
                               productName.includes('broth') || productName.includes('caldo') ||
                               productName.includes('vinegar') || productName.includes('vinagre');
      
      // Determine leftover unit based on product characteristics
      if (isTrulyCountBased) {
        leftoverUnit = 'pcs';
      } else if (isSolidOrPowder) {
        leftoverUnit = 'g'; // Force grams for solids/powders
      } else if (isVolumeProduct && !isSolidOrPowder) {
        leftoverUnit = 'ml';
      } else if (measurementType === 'volume' && !isSolidOrPowder) {
        leftoverUnit = 'ml';
      } else if (measurementType === 'count' && productType === 'unit') {
        leftoverUnit = 'pcs';
      } else {
        leftoverUnit = 'g';
      }
      
      // Calculate leftover values
      const leftoverValue = Math.round(item.leftovers);
      const formattedLeftoverAmount = `${leftoverValue}${leftoverUnit}`;
      
      return {
        productName: item.productName || item.name,
        lang: item.lang || null,
        productId: item.productId,
        selectedSize: item.selectedSize,
        product: item.product,
        leftoverAmount: formattedLeftoverAmount,
        leftoverValue: leftoverValue,
        leftoverUnit: leftoverUnit.trim(),
        leftoverPercentage: item.leftoverPercentage,
        category: item.category,
        perishable: item.perishable,
        shelfLifeDays: item.shelfLifeDays,
        source: item.source || null,
      };
    });
};

/**
 * Determine maximum storage time for the grocery list
 * @param {Array} list - Complete grocery list
 * @returns {String} Maximum storage time description
 */
export const determineMaxStorageTimeMetric = (list) => {
  let maxDays = 0;
  list.forEach((item) => {
    if (item.perishable && item.shelfLifeDays) {
      maxDays = Math.max(maxDays, item.shelfLifeDays);
    }
  });
  return maxDays > 0 ? `${maxDays} days` : "";
};

const GROCERY_SEARCH_PATH = "/api/products/search";

/**
 * Trim product object for AI prompt to reduce token usage
 * Keeps essential matching information while removing verbose data
 * @param {Object} product - Full product object
 * @returns {Object} Trimmed product for prompt
 */
const trimProductForPrompt = (product) => {
  return {
    id: product.id,
    name: product.name,
    displayName: product.displayName,
    category: product.category,
    keywords: product.keywords?.slice(0, 5) || [], // Limit keywords
    sizes: product.sizes?.map(size => ({
      name: size.name,
      description: size.description,
      type: size.type,
      weight: size.weight,
      weightUnit: size.weightUnit,
      avgWeight: size.avgWeight,
      itemsQty: size.itemsQty,
      price: size.price,
      referenceFormat: size.referenceFormat,
      referencePrice: size.referencePrice,
      segment: size.segment
    })) || []
  };
};

/**
 * Trim ingredient object for AI prompt
 * @param {Object} ingredient - Full ingredient object
 * @returns {Object} Trimmed ingredient for prompt
 */
const trimIngredientForPrompt = (ingredient) => {
  return {
    name: ingredient.name,
    originalName: ingredient.originalName,
    allOriginals: ingredient.allOriginals,
    totalQty: ingredient.totalQty,
    unit: ingredient.unit,
    measurementType: ingredient.measurementType,
    originalUnit: ingredient.originalUnit,
    usedIn: ingredient.usedIn
  };
};

/**
 * Use AI to select the best product match from multiple candidates
 * @param {Object} ingredient - The ingredient to match
 * @param {Array} productCandidates - Array of full product objects from Typesense
 * @returns {Promise<Object>} Object with { selectedProduct, certainty, aiReasoning }
 */
const selectBestMatchWithAI = async (ingredient, productCandidates) => {
  if (productCandidates.length === 0) {
    return { selectedProduct: null, certainty: 0.0 };
  }

  if (productCandidates.length === 1) {
    return { selectedProduct: productCandidates[0], certainty: 1.0 };
  }

  try {
    const trimmedIngredient = trimIngredientForPrompt(ingredient);
    const trimmedProducts = productCandidates.map(trimProductForPrompt);

    const prompt = `You are a grocery shopping assistant helping match recipe ingredients to supermarket products.

INGREDIENT NEEDED:
${JSON.stringify(trimmedIngredient, null, 2)}

AVAILABLE PRODUCTS (${trimmedProducts.length} options):
${JSON.stringify(trimmedProducts, null, 2)}

TASK:
1. Analyze the ingredient requirements:
   - Name and variations (originalName, allOriginals)
   - Quantity needed (totalQty, unit)
   - Measurement type (weight/volume/count)
   - Recipe context (usedIn)

2. Compare each product option considering:
   - Name match quality (exact, synonym, or similar)
   - Category appropriateness
   - Product type compatibility (unit/weight/package)
   - Availability of proper measurements (weight, weightUnit, avgWeight)

3. Select the BEST matching product by its ID

4. Assign a certainty score (0.0-1.0) where:
   - 1.0 = Perfect match (exact name, correct type, ideal format)
   - 0.7-0.9 = Good match (synonym or similar, appropriate type)
   - 0.4-0.6 = Acceptable match (category correct, usable format)
   - 0.0-0.3 = Poor match (only vague similarity)

RETURN FORMAT (JSON only, no explanation):
{
  "productId": "selected-product-id",
  "certainty": 0.95,
  "reasoning": "Brief explanation of why this product was chosen"
}`;

    const result = await fetchFromBackend('/grocery-fallback', {
      model: MODEL_CONFIG.ingredients,
      systemPrompt: 'You are a grocery product matching expert. Analyze ingredients and products to find the best match. Always return valid JSON.',
      userPrompt: prompt,
    });

    const content = result.text;

    // Parse AI response
    let aiResult;
    try {
      let jsonContent = content;
      if (jsonContent.startsWith('```json')) {
        jsonContent = jsonContent.slice(7);
      } else if (jsonContent.startsWith('```')) {
        jsonContent = jsonContent.slice(3);
      }
      if (jsonContent.endsWith('```')) {
        jsonContent = jsonContent.slice(0, -3);
      }
      aiResult = JSON.parse(jsonContent.trim());
    } catch (parseError) {
      console.error('Failed to parse AI matching response:', parseError, content);
      return { selectedProduct: productCandidates[0], certainty: 0.5 };
    }

    // Find the selected product by ID
    const selectedProduct = productCandidates.find(p => p.id === aiResult.productId);
    
    if (!selectedProduct) {
      console.warn(`AI selected unknown product ID: ${aiResult.productId}, using first match`);
      return { selectedProduct: productCandidates[0], certainty: 0.5 };
    }

    console.log(`🤖 AI matched "${ingredient.name}" → "${selectedProduct.displayName}" (certainty: ${aiResult.certainty}, reason: ${aiResult.reasoning})`);

    return {
      selectedProduct,
      certainty: aiResult.certainty,
      aiReasoning: aiResult.reasoning
    };

  } catch (error) {
    console.error('Error in AI product matching:', error);
    return { selectedProduct: productCandidates[0], certainty: 0.5 };
  }
};

/**
 * Generate simplified synonyms for ingredients to improve search hits
 * @param {Array} ingredients - Array of ingredient objects
 * @returns {Promise<Object>} Map of ingredient names to their synonyms
 */
const generateIngredientSynonyms = async (ingredients) => {
  try {
    const ingredientNames = ingredients.map(ing => ing.originalName || ing.name);

    const prompt = `You are a grocery shopping expert. For each ingredient below, provide 2-3 simple synonyms that would increase the diversity of the products resultset from a supermarket database.

INGREDIENTS:
${JSON.stringify(ingredientNames, null, 2)}

RULES:
1. Use common supermarket product names (how products are labeled on shelves)
2. Include both English and Spanish translations where applicable
6. Keep synonyms short (1-3 words each)

RETURN FORMAT (JSON only, no explanation):
{
  "ingredient1": "synonym1 synonym2 synonym3",
  "ingredient2": "synonym1 synonym2 synonym3"
}

Example:
{
  "whole milk": "milk leche entera",
  "extra virgin olive oil": "olive oil aceite oliva",
  "free-range eggs": "eggs huevos",
  "fresh basil leaves": "basil albahaca"
}`;

    const result = await fetchFromBackend('/grocery-fallback', {
      model: MODEL_CONFIG.ingredients,
      systemPrompt: 'You are a grocery product naming and selection expert. Generate simplified synonyms to improve product search. Always return valid JSON.',
      userPrompt: prompt,
    });

    const content = result.text;

    // Parse AI response
    let synonymsMap;
    try {
      let jsonContent = content;
      if (jsonContent.startsWith('```json')) {
        jsonContent = jsonContent.slice(7);
      } else if (jsonContent.startsWith('```')) {
        jsonContent = jsonContent.slice(3);
      }
      if (jsonContent.endsWith('```')) {
        jsonContent = jsonContent.slice(0, -3);
      }
      synonymsMap = JSON.parse(jsonContent.trim());
    } catch (parseError) {
      console.error('Failed to parse synonym generation response:', parseError, content);
      return {};
    }

    console.log('🔤 Generated synonyms for ingredients:', synonymsMap);
    return synonymsMap;

  } catch (error) {
    console.error('Error generating ingredient synonyms:', error);
    return {};
  }
};

async function searchIngredientsInTypesense(ingredients, country = "unknown") {
  const endpoint = `${GROCERY_HOST}${GROCERY_SEARCH_PATH}`;

  // Initialize global tracking array for AI matching debugging
  window.ai_matched = [];

  // ========== PASS 1: Simple search with per_page: 1 (no AI) ==========
  console.log('🔍 Pass 1: Simple search for all ingredients...');
  
  const firstPassSearches = ingredients.map((ing) => {
    const originalQuery = ing.originalName || ing.name || ing.allOriginals?.[0] || "";
    console.log(`📝 [Pass 1] Search query: "${originalQuery}"`);
    return {
      collection: "products",
      q: originalQuery,
      query_by: "keywords,displayName,name",
      query_by_weights: "6,3,3",
      per_page: 1,
      prioritize_exact_match: true,
    };
  });

  const firstPassResponse = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      ...(localStorage.getItem('authToken') ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {}),
    },
    body: JSON.stringify({ searches: firstPassSearches }),
  });

  if (!firstPassResponse.ok) {
    throw new Error(`Product search error (Pass 1): ${await firstPassResponse.text()}`);
  }

  const firstPassData = await firstPassResponse.json();

  // Process first pass results - direct matching without AI
  const firstPassResults = ingredients.map((ing, index) => {
    const searchResult = firstPassData.results[index];

    if (searchResult && searchResult.hits && searchResult.hits.length > 0) {
      const hit = searchResult.hits[0];
      const doc = hit.document;
      const meta = typeof findProductMetadata === "function"
        ? findProductMetadata(doc.id || doc.name)
        : null;
      const canonicalCategory = meta
        ? meta.category
        : typeof getIngredientCategory === "function"
        ? getIngredientCategory(ing.name)
        : "Unknown";
      
      const product = buildProductWithSizes(doc, meta, canonicalCategory, ing);
      
      console.log(`✅ [Pass 1] Matched "${ing.name}" → "${product.displayName}"`);

      return {
        ...ing,
        productId: product.id,
        productName: product.displayName,
        lang: product.lang || null,
        category: product.category || canonicalCategory,
        perishable: !!product.perishable,
        shelfLifeDays: product.shelf_life_days || product.shelfLifeDays || 0,
        unmatched: false,
        matchConfidence: 1.0, // Direct match from Pass 1
        aiReasoning: "Direct match from simple search",
        product: product,
      };
    }

    // No match found in first pass
    return {
      ...ing,
      unmatched: true,
      matchConfidence: 0.0,
      category: typeof getIngredientCategory === "function"
        ? getIngredientCategory(ing.name)
        : "Unknown",
    };
  });

  // Separate matched and unmatched
  const matchedResults = firstPassResults.filter(r => !r.unmatched);
  const unmatchedIngredients = firstPassResults.filter(r => r.unmatched);

  console.log(`📊 Pass 1 results: ${matchedResults.length} matched, ${unmatchedIngredients.length} unmatched`);

  // Track unmatched ingredients from Pass 1 in global array
  unmatchedIngredients.forEach(ing => {
    window.ai_matched.push({
      ingredient: ing.originalName || ing.name,
      status: 'unmatched_pass1',
      matchedProduct: null,
      certainty: 0,
      aiReasoning: null,
      timestamp: Date.now()
    });
  });
  console.log('🔍 window.ai_matched after Pass 1:', window.ai_matched);

  // ========== PASS 2: AI-enhanced search for unmatched only ==========
  let secondPassResults = [];
  
  if (unmatchedIngredients.length > 0) {
    console.log('🤖 Pass 2: AI-enhanced search for unmatched ingredients...');
    
    // Generate AI synonyms only for unmatched ingredients
    const synonymsMap = await generateIngredientSynonyms(unmatchedIngredients);
    
    const secondPassSearches = unmatchedIngredients.map((ing) => {
      const originalQuery = ing.originalName || ing.name || ing.allOriginals?.[0] || "";
      
      // Build search terms with AI-generated synonyms
      const synonymsStr = synonymsMap[originalQuery] || synonymsMap[ing.name] || '';
      const synonymsArray = synonymsStr ? synonymsStr.split(' ').filter(s => s.trim()) : [];
      const allTerms = [originalQuery, ...synonymsArray];
      
      // Build OR-delimited search query
      const searchTerms = allTerms.join(' OR ');
      
      console.log(`📝 [Pass 2] Enhanced search for "${originalQuery}": "${searchTerms}"`);

      return {
        collection: "products",
        q: searchTerms,
        query_by: "keywords,displayName,name",
        query_by_weights: "6,3,3",
        per_page: 10,
        prioritize_exact_match: true,
      };
    });

    const secondPassResponse = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...(localStorage.getItem('authToken') ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {}),
      },
      body: JSON.stringify({ searches: secondPassSearches }),
    });

    if (!secondPassResponse.ok) {
      throw new Error(`Product search error (Pass 2): ${await secondPassResponse.text()}`);
    }

    const secondPassData = await secondPassResponse.json();

    // Process second pass with AI selection
    secondPassResults = await Promise.all(unmatchedIngredients.map(async (ing, index) => {
      const searchResult = secondPassData.results[index];

      if (searchResult && searchResult.hits && searchResult.hits.length > 0) {
        // Build full product candidates from all hits
        const productCandidates = searchResult.hits.map(hit => {
          const doc = hit.document;
          const meta = typeof findProductMetadata === "function"
            ? findProductMetadata(doc.id || doc.name)
            : null;
          const canonicalCategory = meta
            ? meta.category
            : typeof getIngredientCategory === "function"
            ? getIngredientCategory(ing.name)
            : "Unknown";
          
          return buildProductWithSizes(doc, meta, canonicalCategory, ing);
        });

        console.log(`🔍 [Pass 2] Found ${productCandidates.length} candidates for "${ing.name}", using AI to select best match...`);

        // Use AI to select the best match from candidates
        const { selectedProduct, certainty, aiReasoning } = await selectBestMatchWithAI(ing, productCandidates);

        if (!selectedProduct) {
          return {
            ...ing,
            unmatched: true,
            matchConfidence: 0.0,
            category: typeof getIngredientCategory === "function"
              ? getIngredientCategory(ing.name)
              : "Unknown",
          };
        }

        const meta = typeof findProductMetadata === "function"
          ? findProductMetadata(selectedProduct.id || selectedProduct.name)
          : null;

        const canonicalCategory = meta
          ? meta.category
          : selectedProduct.category || (typeof getIngredientCategory === "function"
          ? getIngredientCategory(ing.name)
          : "Unknown");

        console.log(`✅ [Pass 2] AI matched "${ing.name}" → "${selectedProduct.displayName}" (certainty: ${certainty})`);

        return {
          ...ing,
          productId: selectedProduct.id,
          productName: selectedProduct.displayName,
          lang: selectedProduct.lang || null,
          category: selectedProduct.category || canonicalCategory,
          perishable: !!selectedProduct.perishable,
          shelfLifeDays: selectedProduct.shelf_life_days || selectedProduct.shelfLifeDays || 0,
          unmatched: false,
          matchConfidence: certainty,
          aiReasoning: aiReasoning,
          product: selectedProduct,
        };
      }

      // Still no match found
      console.log(`❌ [Pass 2] No match found for "${ing.name}"`);
      return {
        ...ing,
        unmatched: true,
        matchConfidence: 0.0,
        category: typeof getIngredientCategory === "function"
          ? getIngredientCategory(ing.name)
          : "Unknown",
      };
    }));
  }

  // Combine results from both passes
  const results = [...matchedResults, ...secondPassResults];

  // Update window.ai_matched with Pass 2 results
  secondPassResults.forEach(result => {
    const ingredientName = result.originalName || result.name;
    const existingEntry = window.ai_matched.find(e => e.ingredient === ingredientName);
    if (existingEntry) {
      existingEntry.status = result.unmatched ? 'unmatched_pass2' : 'matched_by_ai';
      existingEntry.matchedProduct = result.unmatched ? null : result.productName;
      existingEntry.matchedProductId = result.unmatched ? null : result.productId;
      existingEntry.certainty = result.matchConfidence || 0;
      existingEntry.aiReasoning = result.aiReasoning || null;
      existingEntry.updatedAt = Date.now();
    }
  });
  console.log('🤖 window.ai_matched after Pass 2:', window.ai_matched);
  
  console.log(`📊 Final results: ${results.filter(r => !r.unmatched).length} matched, ${results.filter(r => r.unmatched).length} unmatched`);

  // Track successful matches in analytics collection (non-blocking)
  trackMatchedIngredients(results, country).catch((err) =>
    console.warn("Analytics tracking failed:", err)
  );

  return results;
}

/**
 * Track matched and unmatched ingredients in analytics collection
 * @param {Array} results - All ingredients (matched and unmatched)
 * @param {string} country - Country code
 */
async function trackMatchedIngredients(results, country) {
  const successfulMatches = results.filter(
    (item) => !item.unmatched && item.productId && item.product
  );
  
  const unmatchedIngredients = results.filter(
    (item) => item.unmatched
  );

  console.log(
    `📊 Tracking ${successfulMatches.length} matches + ${unmatchedIngredients.length} unmatched for ${country}`
  );

  // Create tracking entries for both matched and unmatched ingredients
  // Matched: ingredient-product pairs with product info
  const matchPairs = successfulMatches.map((ingredient) => {
    const safeName = ingredient.name.replace(/\s+/g, "-").toLowerCase();
    const safeProductId = ingredient.productId.replace(/\s+/g, "-").toLowerCase();
    const analyticsId = `${safeName}_${safeProductId}_${country}`;
    
    return {
      analyticsId,
      ingredient,
      product: ingredient.product,
      isMatched: true,
    };
  });
  
  // Unmatched: ingredient-only tracking (no product)
  const unmatchedPairs = unmatchedIngredients.map((ingredient) => {
    const safeName = ingredient.name.replace(/\s+/g, "-").toLowerCase();
    const analyticsId = `${safeName}_unmatched_${country}`;
    
    return {
      analyticsId,
      ingredient,
      product: null,
      isMatched: false,
    };
  });
  
  const allPairs = [...matchPairs, ...unmatchedPairs];
  
  if (allPairs.length === 0) return;

  // Fetch existing documents to get current counts
  const existingCounts = {};

  try {
    // Batch fetch existing documents (404s are expected for new ingredients)
    const fetchPromises = allPairs.map(async ({ analyticsId }) => {
      const encodedId = encodeURIComponent(analyticsId);
      try {
        const response = await fetch(
          `${GROCERY_HOST}/api/analytics/${encodedId}`,
          {
            method: "GET",
            headers: {
              "Accept": "application/json",
              ...(localStorage.getItem('authToken') ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {}),
            },
          }
        );
        // 404 is expected for new documents - don't treat as error
        if (response.ok) {
          const doc = await response.json();
          existingCounts[analyticsId] = {
            occurrence_count: doc.occurrence_count || 0,
            ingredient_search_count: doc.ingredient_search_count || 0,
            first_matched_timestamp: doc.first_matched_timestamp,
          };
        }
        // Silently ignore 404s - they're expected for first-time matches
      } catch (error) {
        // Network errors or parsing errors - silently ignore
      }
    });

    await Promise.allSettled(fetchPromises);
  } catch (error) {
    // Silently continue - we'll create documents with count 1
  }

  // Prepare documents with incremented counts
  const documentsToUpsert = allPairs.map(({ analyticsId, ingredient, product, isMatched }) => {
    const existing = existingCounts[analyticsId];
    
    // Increment both counters:
    // - ingredient_search_count: how many times we searched for this ingredient name
    // - occurrence_count: how many times this ingredient matched a product (0 for unmatched)
    const newOccurrenceCount = isMatched ? (existing?.occurrence_count || 0) + 1 : 0;
    const newSearchCount = (existing?.ingredient_search_count || 0) + 1;

    const doc = {
      id: analyticsId,
      // Ingredient info
      ingredient_name: ingredient.originalName || ingredient.name,
      normalized_name: ingredient.name,
      measurement_type: ingredient.measurementType || "unknown",
      // Counters
      occurrence_count: newOccurrenceCount, // How many times THIS ingredient matched (0 for unmatched)
      ingredient_search_count: newSearchCount, // How many times we searched for THIS ingredient name
      // Metadata
      country: country,
      last_matched_timestamp: Date.now(),
      first_matched_timestamp: existing?.first_matched_timestamp || Date.now(),
    };
    
    // Add product info only for matched ingredients
    if (isMatched && product) {
      doc.product_id = ingredient.productId;
      doc.product_name = ingredient.productName;
      doc.product_display_name = product.displayName || ingredient.productName;
      doc.product_original_name = product.name || "";
      doc.product_thumbnail = product.thumbnail || "";
    } else {
      // For unmatched ingredients, use "unmatched" as product_id
      doc.product_id = "unmatched";
      doc.product_name = "";
      doc.product_display_name = "";
      doc.product_original_name = "";
      doc.product_thumbnail = "";
    }
    
    return doc;
  });

  try {
    // Use import endpoint for batch upsert
    const importResponse = await fetch(
      `${GROCERY_HOST}/api/analytics/import`,
      {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          "Accept": "text/plain",
          ...(localStorage.getItem('authToken') ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {}),
        },
        // Each document as a JSON line
        body: documentsToUpsert.map((doc) => JSON.stringify(doc)).join("\n"),
      }
    );

    if (!importResponse.ok) {
      const errorText = await importResponse.text();
      console.warn("Failed to batch import analytics:", errorText);
    } else {
      const results = await importResponse.text();
      const successCount = results.split("\n").filter(line => {
        try {
          return JSON.parse(line).success === true;
        } catch {
          return false;
        }
      }).length;
      console.log(`✅ Analytics tracking completed (${successCount}/${allPairs.length} entries: ${matchPairs.length} matched + ${unmatchedPairs.length} unmatched)`);
    }
  } catch (error) {
    // Silently fail - don't disrupt the main flow
    console.warn("Failed to track ingredients:", error.message);
  }
}
